/**
 * Main Map component
 *
 * Displays Google Map with neighborhood polygons and listing markers
 */
import { formatNeighborhoodName, formatListingAddress, factsFromListingSummary, factsFromTmkStatusMarker, isAgriculturalZoning, listingItemKey } from '@slowmaui/shared-types';
import { getMarketTrendsColor, MARKET_TRENDS_NO_DATA_COLOR } from '../marketTrendsColors.js';
import { getClimateColor, CLIMATE_RAMPS, CLIMATE_NO_DATA_COLOR } from '../climateColors.js';
import { ATTRIBUTE_REGISTRY } from '../attributes/registry.js';
import Supercluster from 'supercluster';
// Below this many sales in the trailing 12 months, a scope renders as
// MARKET_TRENDS_NO_DATA_COLOR instead of a real point on the price ramp -
// a single closing (or even 2) isn't a reliable "median" for that area.
const MARKET_TRENDS_MIN_SAMPLE_SIZE = 3;
const MARKET_TRENDS_FILL_OPACITY = 0.55;
const CLIMATE_FILL_OPACITY = 0.55;
// Floating label constants (from v1)
const FLOATING_LABEL_OFFSET = 15; // Pixels above polygon top
const FLOATING_LABEL_MIN_TOP = 110; // Min distance from viewport top (header + filter drawer)
// Building extrusion (fake 2.5D effect for neighborhood-mode building footprints)
const BUILDING_EXTRUSION_PX_PER_FLOOR = 9; // Screen-space px per floor, at BUILDING_EXTRUSION_REFERENCE_ZOOM
const BUILDING_EXTRUSION_REFERENCE_ZOOM = 20; // Zoom level BUILDING_EXTRUSION_PX_PER_FLOOR was tuned at (typical close-up building-viewing zoom, not NEIGHBORHOOD_MODE_ZOOM - that's just the mode's entry zoom, much wider than where buildings are actually inspected)
const BUILDING_EXTRUSION_DIRECTION = { dx: -0.34, dy: -0.94 }; // Up-and-slightly-left; normalized at use time
const BUILDING_EXTRUSION_STUB_FLOORS = 1; // Assumed height (rendering only) when a building has no floors on record
const BUILDING_EXTRUSION_CAP_FLOORS = 8; // Height compresses (sqrt) past this many floors so tall buildings stay legible
// One color per floor, cycling every 5 (floor 6 reuses floor 1's color, etc).
// Order + hues validated with the dataviz skill's validate_palette.js: OKLCH
// lightness band and chroma floor pass individually; this order was chosen
// (of 120 candidates) to maximize the worst-case adjacent CVD separation
// around the ring, since the cycle can place any two colors next to each
// other depending on a building's floor count — worst ring-adjacent CVD
// deltaE 11.0 (target 8.0), worst normal-vision deltaE 15.2 (floor 15.0, a
// thin margin — chroma is trimmed to close to the minimum that still keeps
// all 5 tellable apart; there isn't much more room to mute this further
// without merging colors together).
const BUILDING_FLOOR_COLORS = ['#5ea8ee', '#80c376', '#a68be1', '#e37d8c', '#dda94a'];
const BUILDING_FLOOR_LINE_COLOR = '#6b7280';
const BUILDING_EXTRUSION_MAX_FLOOR_LINES = 40; // Safety cap against bad data (e.g. floors typo'd as 900)
const DEFAULT_CENTER = { lat: 20.7644, lng: -156.4450 }; // South Maui
const DEFAULT_ZOOM = 12; // Closer view for listings
// Desktop's wider viewport shows a much larger geographic area than
// mobile's narrow one at the same zoom level, so at a shared default zoom
// far more listings (and cluster bubbles) fit on screen on load for
// desktop specifically. One level tighter keeps the visible area - and
// bubble count - closer to what mobile shows by default.
const DEFAULT_ZOOM_DESKTOP = 13;
const MAP_ID = '12a31a868548f2ef2fa45cfa'; // v1 custom style
// Initial camera when the caller doesn't request a specific center/zoom
// (apps/public and apps/agent both currently don't) - fits Maui Island
// specifically (west-maui/wailuku-kahului/kihei-makena/paia-haiku/
// makawao-pukalani-kula/hana), deliberately excluding Molokai/Lanai despite
// Market Stats now rolling up county-wide - the initial view is what most
// visitors actually want to see, not the full county extent. Computed from
// the real combined extent of those 6 regions' polygons (confirmed live
// 2026-10: lat 20.575-21.031, lng -156.697--155.979) plus ~0.05deg padding
// on each side so no region's edge sits flush against the viewport border.
// A LatLngBounds + fitBounds(), not a static center/zoom - fitBounds
// derives the correct zoom for whatever the actual container size/aspect
// ratio is (mobile vs desktop vs a drawer-narrowed layout), which a single
// hardcoded zoom number can't do reliably across all of them.
const MAUI_ISLAND_VIEW_BOUNDS = { south: 20.525, west: -156.747, north: 21.081, east: -155.929 };
// Neighborhood zoom constants (from v1)
// NEIGHBORHOOD_MARKERS_MIN_ZOOM doubles as the Supercluster "handoff" zoom
// (see updateListingLayer's useUnmapped) - below it, the generic listing
// layer includes every listing regardless of neighborhood membership;
// at/above it, neighborhood badges take over representing in-neighborhood
// listings and the generic layer excludes them. Bumped up from 13.5 (was
// tuned only for when the badge itself should appear) so that by the time
// the handoff happens, Supercluster's own per-area counts are already
// close to what the neighborhood badges are about to show - a starting
// value, needs live tuning against real listing density.
const NEIGHBORHOOD_MARKERS_MIN_ZOOM = 14; // Show neighborhood markers
const NEIGHBORHOOD_LABELS_ZOOM = 15; // Show labels on markers
const NEIGHBORHOOD_MODE_ZOOM = 16; // Enter neighborhood mode
// Exit is now dynamic based on polygon height vs viewport (50% threshold)
// Region/district label scaling - full size at/above MAX_ZOOM (where the
// base sizes below were tuned to look right), shrinking linearly down to
// MIN_SCALE at/below MIN_ZOOM (full island/multi-region view, where
// full-size labels crowd each other and dominate the map). MIN_ZOOM raised
// from 7 to 9 - the map's hard floor (minZoom: 6) is never where someone
// actually stops zooming out; a real "see the whole island" view sits
// around zoom 9-10, and at the old MIN_ZOOM=7 that view was still at
// 67-78% scale (barely shrunk) because the floor sat below where anyone
// ever really zooms. MAX_ZOOM nudged to 13 to land "full size" at roughly
// the halfway point of the map's overall practical zoom journey
// (minZoom 6 up to neighborhood close-ups ~18-20), per Todd's framing.
const GEOGRAPHY_LABEL_MIN_ZOOM = 9;
const GEOGRAPHY_LABEL_MAX_ZOOM = 13;
const GEOGRAPHY_LABEL_MIN_SCALE = 0.4;
// Neighborhood label scaling - same shrink-toward-min-zoom idea as region/
// district labels above, just re-anchored to the zoom range neighborhood
// labels actually live in (they don't appear at all below
// NEIGHBORHOOD_LABELS_ZOOM). Areas with many small, adjacent neighborhoods
// (Wailea, Kihei) get crowded well before "true" close-up zoom, so this
// keeps labels small right as they first appear and only grows them back
// out once you're zoomed into roughly one neighborhood's worth of area.
const NEIGHBORHOOD_LABEL_MIN_ZOOM = NEIGHBORHOOD_LABELS_ZOOM;
const NEIGHBORHOOD_LABEL_MAX_ZOOM = 18;
const NEIGHBORHOOD_LABEL_MIN_SCALE = 0.6;
// Was 14 - one shared constant so createNeighborhoodMarkerContent's initial
// render and rescaleNeighborhoodLabels' per-zoom-tick update can't drift
// out of sync with each other (they used to each hardcode the same number).
const NEIGHBORHOOD_LABEL_FONT_SIZE_BASE = 16;
function computeLabelScale(zoom, minZoom, maxZoom, minScale) {
    if (zoom >= maxZoom)
        return 1;
    if (zoom <= minZoom)
        return minScale;
    const t = (zoom - minZoom) / (maxZoom - minZoom);
    return minScale + t * (1 - minScale);
}
function computeGeographyLabelScale(zoom) {
    return computeLabelScale(zoom, GEOGRAPHY_LABEL_MIN_ZOOM, GEOGRAPHY_LABEL_MAX_ZOOM, GEOGRAPHY_LABEL_MIN_SCALE);
}
function computeNeighborhoodLabelScale(zoom) {
    return computeLabelScale(zoom, NEIGHBORHOOD_LABEL_MIN_ZOOM, NEIGHBORHOOD_LABEL_MAX_ZOOM, NEIGHBORHOOD_LABEL_MIN_SCALE);
}
// One dark, muted color per region (by region id) - carried over to that
// region's districts too, so the whole hierarchy reads as one family at a
// glance. All same lightness/saturation (dark, desaturated) so none reads
// as more "important" than another, and chosen to sit clear of the
// existing status-pin hues (green/orange/light-blue - see STATUS_COLORS)
// so region labels never look like they're conveying listing status.
const REGION_LABEL_COLORS = {
    'west-maui': '#2b455f', // dark slate blue
    'wailuku-kahului': '#5f3c2b', // dark brown
    'kihei-makena': '#5f2b3c', // dark wine
    'paia-haiku': '#2b5f52', // dark teal
    'makawao-pukalani-kula': '#455f2b', // dark olive
    'hana': '#452b5f', // dark violet
};
const GEOGRAPHY_LABEL_FALLBACK_COLOR = '#37414f'; // dark slate gray - unmapped region/no regionId
const COUNTY_LABEL_COLOR = '#1a2332'; // near-black navy - one level above every region's own color family
// District boundary line - deliberately light/quiet (a reference line, not
// a bold shape competing with neighborhood polygons or listing pins), and
// grey rather than the region-family colors so it doesn't imply the
// boundary itself is a "region" indicator.
const DISTRICT_OUTLINE_COLOR = '#9ca3af';
function resolveGeographyLabelColor(entity, level) {
    if (level === 'county')
        return COUNTY_LABEL_COLOR;
    const regionId = level === 'region' ? entity.id : entity.regionId;
    return (regionId && REGION_LABEL_COLORS[regionId]) || GEOGRAPHY_LABEL_FALLBACK_COLOR;
}
function hexToRgba(hex, alpha) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
// One-shot "look at me" pulse on the floating neighborhood-mode label right
// after it appears - a ring that expands off the badge's green and a small
// scale bounce, 3 times (~2.4s) then it stops on its own via 'animationend'
// (see createFloatingLabel) so it doesn't nag anyone who's already looking
// at a static map. Injected once as a real <style> tag rather than inline -
// @keyframes can't go in a cssText string - matching the injectStyles()
// pattern every other ui-components component uses (e.g. ClimateLegend).
const NEIGHBORHOOD_LABEL_ATTENTION_STYLE_ID = 'sm-nhm-floating-label-attention-styles';
const NEIGHBORHOOD_LABEL_ATTENTION_CSS = `
@keyframes sm-nhm-label-attention-pulse {
  0% { box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3), 0 0 0 0 rgba(34, 197, 94, 0.7); transform: translateX(-50%) scale(1); }
  50% { box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3), 0 0 0 9px rgba(34, 197, 94, 0); transform: translateX(-50%) scale(1.07); }
  100% { box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3), 0 0 0 0 rgba(34, 197, 94, 0); transform: translateX(-50%) scale(1); }
}
.neighborhood-floating-label--attention {
  animation: sm-nhm-label-attention-pulse 0.8s ease-out 3;
}
`;
// Region labels stay slightly more opaque than their districts - same hue
// family, but regions read as the "bolder" level, matching the font-size/
// weight distinction below.
const GEOGRAPHY_LABEL_BASE = {
    county: { fontSize: 17, padV: 9, padH: 16, fontWeight: 800, letterSpacing: '0.03em', alpha: 0.9 },
    region: { fontSize: 15, padV: 8, padH: 14, fontWeight: 700, letterSpacing: '0.02em', alpha: 0.85 },
    district: { fontSize: 13, padV: 6, padH: 11, fontWeight: 600, letterSpacing: 'normal', alpha: 0.68 },
};
// Polygon styles - unfilled by default, filled only when Zoning is ON
const POLYGON_STYLES = {
    default: {
        strokeColor: '#2563eb',
        strokeWeight: 2,
        strokeOpacity: 0.8,
        fillColor: '#3b82f6',
        fillOpacity: 0, // No fill by default
    },
    hover: {
        strokeColor: '#1d4ed8',
        strokeWeight: 3,
        strokeOpacity: 1,
        fillColor: '#2563eb',
        fillOpacity: 0.15, // Light fill on hover
    },
    selected: {
        strokeColor: '#059669',
        strokeWeight: 3,
        strokeOpacity: 1,
        fillColor: '#10b981',
        fillOpacity: 0.2,
    },
    strAllowed: {
        strokeColor: '#7c3aed',
        strokeWeight: 2,
        strokeOpacity: 0.8,
        fillColor: '#8b5cf6',
        fillOpacity: 0, // No fill by default
    },
};
// Zoning colors from v1 (fill opacity 0.35 when enabled)
const ZONING_COLORS = {
    'Hotel': { fill: '#3b82f6', stroke: '#2563eb' }, // Blue
    'Apartment': { fill: '#8b5cf6', stroke: '#7c3aed' }, // Purple
    'Apartment*': { fill: '#f59e0b', stroke: '#d97706' }, // Orange (restricted)
    'Residential': { fill: '#34d399', stroke: '#10b981' }, // Green
};
const ZONING_FILL_OPACITY = 0.35;
function normalizeZoningCategory(rawZoning) {
    if (!rawZoning)
        return 'Residential';
    const upper = rawZoning.toUpperCase().trim();
    if (upper === 'APARTMENT*')
        return 'Apartment*';
    if (upper.startsWith('H') || upper.includes('HOTEL') || upper.includes('RESORT'))
        return 'Hotel';
    if (upper.startsWith('A-') || upper === 'APT' || upper.includes('APARTMENT'))
        return 'Apartment';
    if (upper.startsWith('R-') || upper.includes('RESIDENTIAL'))
        return 'Residential';
    // Unknown designations (D-2, Condo, etc.) fall through to Residential so
    // they're toggled by the most-conservative checkbox — same fallback the
    // legacy color path used, just now applied to visibility as well.
    return 'Residential';
}
// ==================== "Other" catch-all polygon holes ====================
//
// "Other (<District>)" neighborhoods are the catch-all for TMKs in a
// district with no subdiv_condo label (see Find & Assign Neighborhoods on
// the admin side) - their polygon is a convex hull over those TMKs' points,
// which can easily enclose a real, more specific neighborhood sitting
// inside that same area. A lower zIndex (see addNeighborhood) fixes
// stroke/label stacking, but two semi-transparent fills over the same
// pixels still visually blend into a darker "double fill" regardless of
// paint order - z-index alone doesn't fix that.
//
// The fix: google.maps.Polygon natively supports holes - any ring in
// `paths` wound opposite the outer ring is treated as a cutout, and the
// renderer's own fill rule does a real "inside outer, outside all holes"
// test per pixel. So every other neighborhood whose polygon might overlap
// "Other" gets added as a hole ring (winding-corrected), and the renderer
// does the actual geometric subtraction for free - no clipping math, no new
// dependency, computed once here at polygon-creation time (see
// getHoleRingsFor). A hole ring doesn't need to be pre-clipped to the outer
// ring for this to be correct - the fill rule only subtracts wherever the
// two actually overlap, regardless of how far the hole extends beyond it.
function isOtherCatchAllNeighborhood(name) {
    return /^other\s*\(/i.test(name.trim());
}
function ringBounds(ring) {
    let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
    for (const p of ring) {
        if (p.lat < minLat)
            minLat = p.lat;
        if (p.lat > maxLat)
            maxLat = p.lat;
        if (p.lng < minLng)
            minLng = p.lng;
        if (p.lng > maxLng)
            maxLng = p.lng;
    }
    return { minLat, maxLat, minLng, maxLng };
}
function boundsOverlap(a, b) {
    return a.minLat <= b.maxLat && a.maxLat >= b.minLat && a.minLng <= b.maxLng && a.maxLng >= b.minLng;
}
// Shoelace formula - only the sign matters here (to detect/flip winding),
// not the actual area.
function signedArea(ring) {
    let sum = 0;
    for (let i = 0; i < ring.length; i++) {
        const p1 = ring[i];
        const p2 = ring[(i + 1) % ring.length];
        sum += (p2.lng - p1.lng) * (p2.lat + p1.lat);
    }
    return sum;
}
// Google Maps treats a ring as a hole when it's wound opposite the outer
// ring - flip it if it isn't already, so it's guaranteed to actually cut a
// hole regardless of whatever point order the source data happened to use.
function asOppositeWinding(outerRing, ring) {
    const outerSign = Math.sign(signedArea(outerRing));
    const ringSign = Math.sign(signedArea(ring));
    return ringSign === outerSign ? [...ring].reverse() : ring;
}
// Standard ray-casting point-in-polygon test - used to find which
// neighborhoods sit inside a given district (see getDistrictHoleRings).
function pointInRing(point, ring) {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const pi = ring[i];
        const pj = ring[j];
        const intersects = (pi.lat > point.lat) !== (pj.lat > point.lat) &&
            point.lng < ((pj.lng - pi.lng) * (point.lat - pi.lat)) / (pj.lat - pi.lat) + pi.lng;
        if (intersects)
            inside = !inside;
    }
    return inside;
}
// Marker colors by status - v1 palette (for MLS listings outside NHM)
const STATUS_COLORS = {
    Active: '#22c55e', // Green (For Sale)
    Pending: '#f59e0b', // Orange
    Sold: '#93c5fd', // Light Blue (Recently Sold)
    Withdrawn: '#9ca3af', // Gray (Off Market)
    Expired: '#9ca3af',
    Cancelled: '#9ca3af',
};
// NHM marker colors by computed status
const NHM_STATUS_COLORS = {
    ForSale: '#22c55e', // Green
    Pending: '#f59e0b', // Orange
    RecentlySold: '#93c5fd', // Light Blue
    OffMarket: '#9ca3af', // Gray
};
// Dimmed marker colors for filtered-out properties (v1 style)
const NHM_DIMMED_COLORS = {
    ForSale: '#c8e6c9', // Light Green (faded)
    Pending: '#fde68a', // Light Yellow/Orange (faded)
    RecentlySold: '#dbeafe', // Very Light Blue (faded)
    OffMarket: '#d1d5db', // Light Gray (faded)
};
// Dimmed neighborhood label color
// Neighborhood label color is always #2c5530, with opacity 0.3 when dimmed
// Cluster colors - v1 green palette
const CLUSTER_COLOR = '#22c55e';
// Badge size (scaled 1.5x on desktop like v1, on top of this base)
const BADGE_SIZE_BASE = 15;
export class SlowMauiMap {
    options;
    state;
    badgeOptions = {
        show: Object.fromEntries(ATTRIBUTE_REGISTRY.map((entry) => [entry.id, false])),
        favoriteIds: new Set(),
        alertIds: new Set(),
        agentPickIds: new Set(),
        featuredIds: new Set(),
    };
    // Performance optimization: cached marker library and state tracking
    markerLibrary = null;
    // "My Location" blue-dot marker (Google-Maps-style) - created lazily on
    // first setUserLocationMarker(data) call, reused/updated in place on
    // subsequent calls (watchPosition fires repeatedly), removed via
    // setUserLocationMarker(null).
    userLocationMarker = null;
    userLocationAccuracyCircle = null;
    userLocationHeadingEl = null;
    lastNeighborhoodLabelsVisible = null;
    // Per-neighborhood "what was last rendered" signature (showLabels + count),
    // so an on-screen marker whose inputs haven't actually changed since last
    // render skips the DOM rebuild too — the off-screen skip in
    // showNeighborhoodMarkers() only helps neighborhoods off-screen, not the
    // common "panned slightly, same couple neighborhoods, nothing changed" case.
    lastNeighborhoodMarkerSignature = new Map();
    zoomDebounceTimer = null;
    // Coalesces the "cheap" per-tick zoom_changed work (updateVisibility,
    // updateListingLayer, rescaleGeographyLabels, rescaleNeighborhoodLabels)
    // into one execution per animation frame instead of one per native
    // zoom_changed event - with isFractionalZoomEnabled, a single pinch
    // gesture fires that event far more often than the browser actually
    // paints, so running all four synchronously on every tick competes with
    // the gesture's own camera animation for the main thread. See
    // scheduleZoomTick().
    pendingZoomTickRaf = null;
    // Last booleans updateVisibility() computed, so a tick that doesn't cross
    // a visibility threshold can skip both of its marker loops entirely.
    lastVisibilityState = null;
    cachedListingsByNeighborhood = null;
    // Per-district hole rings (neighborhoods contained within), for punching
    // neighborhoods out of the district's own Overlay fill - see
    // getDistrictHoleRings. Invalidated whenever districts or neighborhoods
    // reload, since both feed the geometry.
    districtHoleRingsCache = null;
    // Supercluster indexes backing the Active/Pending listing layer - two per
    // status, keyed `${status}:all` / `${status}:unmapped` (see
    // rebuildClusterIndexes for why two, and updateListingLayer for which one
    // gets queried at a given zoom).
    clusterIndexes = new Map();
    // Signature of whatever inputs affect what updateListingLayer renders -
    // gates marker teardown/rebuild so a same-zoom zoom_changed tick doesn't
    // churn markers. See updateListingLayer.
    lastListingLayerSignature = null;
    // Signature (ordered list of ids) of whatever setListings() last actually
    // rendered - a caller passing the identical set again (redundant, not
    // just same-zoom) is a full no-op instead of a full teardown+rebuild.
    // Confirmed live: apps/public's init() and the map's own first idle-
    // triggered viewport reload could redraw every marker on the map two or
    // three times in a row for the exact same data on first load - see
    // setListings.
    lastSetListingsSignature = null;
    // Bumped at the start of every renderListingLayer call - lets an
    // in-flight call detect it's been superseded by a newer one once its
    // await on getMarkerLibrary() resolves (only a real wait on the very
    // first call ever; cached after that), so it can bail out without
    // clobbering whatever the newer call already rendered.
    listingLayerRenderId = 0;
    // Track viewport height to detect zoom direction for neighborhood mode exit
    previousViewportHeight = null;
    neighborhoodModeExitDebounceTimer = null;
    // Guard against race conditions during NHM entry
    enteringNeighborhoodMode = false;
    // Width (px) of UI chrome currently covering the right edge of the map
    // (the UnifiedDrawer, when open) - kept in sync by the caller via
    // setRightInset() so fit-bounds calls like enterNeighborhoodMode's can
    // fit content into the visible map area instead of the full viewport.
    rightInsetPx = 0;
    constructor(options) {
        this.options = options;
        this.state = {
            map: null,
            neighborhoods: [],
            neighborhoodsVisible: true,
            neighborhoodOutlineEnabled: false,
            badgesForceHidden: false,
            zoningEnabled: false,
            zoningCategories: {
                'Residential': true,
                'Apartment': true,
                'Apartment*': true,
                'Hotel': true,
            },
            buildingExtrusionEnabled: false,
            offMarketUnitLabelsEnabled: false,
            polygons: new Map(),
            labels: new Map(),
            badges: new Map(),
            neighborhoodMarkers: new Map(),
            selectedNeighborhoodId: null,
            neighborhoodMode: {
                active: false,
                neighborhoodId: null,
                neighborhood: null,
                polygon: null,
                polygonBounds: null,
                propertyMarkers: [],
                buildingMarkers: [],
                buildingPolygons: [],
                buildingFloorLines: [],
                buildingSource: [],
                floatingLabel: null,
                tmkStatusMarkers: [],
                matchingForSaleCount: 0,
            },
            counties: [],
            regions: [],
            districts: [],
            countyMarkers: new Map(),
            regionMarkers: new Map(),
            districtMarkers: new Map(),
            districtPolygons: new Map(),
            districtOutlines: new Map(),
            // Districts have always shown their name bubbles (no toggle existed
            // before this feature) - default true to match, same as
            // neighborhoodsVisible below. DisplayDrawer's initialOptions default
            // must stay in sync with this (no automatic initial sync call exists
            // between them - see neighborhoodsVisible/satellite/zoning, which
            // already rely on matching defaults rather than a push on construct).
            districtsEnabled: true,
            districtOutlineEnabled: false,
            neighborhoodFillMode: 'none',
            districtFillMode: 'none',
            priceMetricData: {
                medianSalePrice: { neighborhoods: new Map(), districts: new Map() },
                currentForSale: { neighborhoods: new Map(), districts: new Map() },
            },
            activePriceMetric: 'medianSalePrice',
            neighborhoodPriceData: new Map(),
            districtPriceData: new Map(),
            neighborhoodPriceRange: { min: 0, center: 0, max: 0 },
            districtPriceRange: { min: 0, center: 0, max: 0 },
            climateDatatypeData: {
                rainfall: { neighborhoods: new Map(), districts: new Map() },
                temperature: { neighborhoods: new Map(), districts: new Map() },
            },
            activeClimateDatatype: 'rainfall',
            neighborhoodClimateData: new Map(),
            districtClimateData: new Map(),
            neighborhoodClimateRange: { min: 0, center: 0, max: 0 },
            districtClimateRange: { min: 0, center: 0, max: 0 },
            listings: [],
            listingMarkers: [],
            clusterMarkers: [],
            selectedListingId: null,
            hoveredListingId: null,
            activeStatuses: new Set(['Active']), // Default: only For Sale (matches CNTL drawer defaults)
            propertyFilters: {}, // No filters by default
        };
    }
    async initialize() {
        const { Map } = await google.maps.importLibrary('maps');
        // Zoom control stays off on mobile (thumb gestures cover it, and screen
        // space is tighter), but desktop has both the room and the mouse-driven
        // interaction model where a visible +/- is genuinely useful alongside
        // scroll-to-zoom.
        const isDesktop = window.innerWidth >= 1024;
        this.state.map = new Map(this.options.container, {
            center: this.options.center ?? DEFAULT_CENTER,
            zoom: this.options.zoom ?? (isDesktop ? DEFAULT_ZOOM_DESKTOP : DEFAULT_ZOOM),
            mapId: MAP_ID,
            mapTypeId: this.options.mapType ?? 'roadmap',
            mapTypeControl: false,
            fullscreenControl: false,
            streetViewControl: false,
            rotateControl: false,
            tilt: 0,
            gestureHandling: 'greedy',
            isFractionalZoomEnabled: true,
            // Backstop against the camera ever reaching Google's literal zoom-0
            // whole-Earth view - one notch below GEOGRAPHY_LABEL_MIN_ZOOM (7,
            // "full island/multi-region view"), so legitimate zoom-out is
            // unaffected but a bad fitBounds/setCenter call (e.g. from invalid
            // coordinates) can't zoom out past the island chain.
            minZoom: 6,
            zoomControl: isDesktop,
            zoomControlOptions: isDesktop ? { position: google.maps.ControlPosition.LEFT_BOTTOM } : undefined,
        });
        // No explicit center/zoom from the caller -> show Maui Island instead
        // of the static South-Maui default (see MAUI_ISLAND_VIEW_BOUNDS). A
        // caller that DOES pass center/zoom (e.g. a deep link into one
        // neighborhood) still gets exactly that, unchanged.
        if (!this.options.center && !this.options.zoom) {
            this.state.map.fitBounds(MAUI_ISLAND_VIEW_BOUNDS);
        }
        // Add zoom change listener for label/marker visibility and cluster/marker switching
        // Uses debouncing for expensive operations to prevent jank during zoom animations
        this.state.map.addListener('zoom_changed', () => {
            // Per-tick visibility/label/listing-layer work is rAF-coalesced - see
            // scheduleZoomTick() - rather than run synchronously here, since a
            // single pinch gesture fires zoom_changed many more times per second
            // than the browser actually paints.
            this.scheduleZoomTick();
            // Auto-exit neighborhood mode when polygon becomes too small in viewport
            // Debounce to avoid false exits during pinch-zoom animations on mobile
            if (this.state.neighborhoodMode.active) {
                if (this.neighborhoodModeExitDebounceTimer) {
                    clearTimeout(this.neighborhoodModeExitDebounceTimer);
                }
                this.neighborhoodModeExitDebounceTimer = setTimeout(() => {
                    this.checkNeighborhoodModeExit();
                    this.neighborhoodModeExitDebounceTimer = null;
                }, 150); // 150ms debounce for exit check
            }
            // Expensive operation - debounce to avoid jank during continuous zoom
            if (this.zoomDebounceTimer) {
                clearTimeout(this.zoomDebounceTimer);
            }
            this.zoomDebounceTimer = setTimeout(() => {
                this.updateNeighborhoodMarkers();
                this.zoomDebounceTimer = null;
            }, 100); // 100ms debounce
        });
        // Add bounds change listener
        this.state.map.addListener('idle', () => {
            this.handleBoundsChanged();
        });
        // Add center/zoom change listener for floating label positioning
        this.state.map.addListener('center_changed', () => {
            this.updateFloatingLabelPosition();
        });
        if (this.options.onMapReady) {
            this.options.onMapReady(this.state.map);
        }
    }
    // ==================== LISTINGS ====================
    // Zoom threshold for showing Sold/Off-market listings (configurable)
    // Set high (18) so these only appear at very close zoom
    static SOLD_ZOOM_THRESHOLD = 18;
    // Supercluster clustering radius, in on-screen pixels - the distance
    // within which points merge into one bubble. Unlike the old fixed
    // lat/lng grid, Supercluster clusters in real projected screen space, so
    // cells stay a consistent visual size at every zoom, and a cluster
    // shrinks continuously down to a plain individual marker as you zoom in
    // rather than snapping between two rendering modes. One dial to tune
    // coarser/finer.
    // NOTE: Supercluster only computes cluster trees at *integer* zoom levels
    // (see its _limitZoom) - there's no interpolation between them, only
    // discrete snapshots. A too-large radius means the real-world merge
    // distance shrinks a lot between consecutive integer zooms (e.g. 100px
    // covers ~3.5km at zoom 12 but only ~900m at zoom 14), so most points
    // cross their individual merge/split threshold within the same narrow
    // 1-level window and appear to "pop" apart all at once instead of
    // fragmenting gradually across several zoom levels. Smaller radius
    // spreads that transition out more (closer to Supercluster's own
    // default of 40), at the cost of somewhat more bubbles at the most
    // zoomed-out views.
    static CLUSTER_RADIUS = 48;
    // Wider radius on narrow viewports - fewer, bigger clusters at the same
    // zoom means less marker-creation work by default on phones, which tend
    // to pair weaker CPUs with slower connections than the desktop case this
    // constant was originally tuned for. Same >=1024 breakpoint already used
    // elsewhere in this file for badge scaling (see isDesktop).
    static CLUSTER_RADIUS_MOBILE = 72;
    // Zoom above which Supercluster stops merging points into new clusters -
    // set comfortably above where clusters naturally thin out to individual
    // pins on their own; this just bounds how deep the index goes.
    static CLUSTER_MAX_ZOOM = 16;
    get clusterRadius() {
        return window.innerWidth >= 1024 ? SlowMauiMap.CLUSTER_RADIUS : SlowMauiMap.CLUSTER_RADIUS_MOBILE;
    }
    // How many individual markers renderListingLayer creates/updates before
    // yielding to the browser (see the chunked loop there) - a dense area
    // breaking out of a cluster can mean hundreds of markers at once, and
    // building all of them in one synchronous task is exactly the kind of
    // main-thread block that reads as the map "hanging". Smaller on mobile
    // for the same reason the cluster radius is wider there - a weaker CPU
    // takes longer per marker, so a smaller count keeps each chunk's actual
    // wall-clock time roughly comparable across devices instead of using one
    // fixed count everywhere.
    static MARKER_CHUNK_SIZE = 150;
    static MARKER_CHUNK_SIZE_MOBILE = 60;
    get markerChunkSize() {
        return window.innerWidth >= 1024 ? SlowMauiMap.MARKER_CHUNK_SIZE : SlowMauiMap.MARKER_CHUNK_SIZE_MOBILE;
    }
    yieldToFrame() {
        return new Promise((resolve) => requestAnimationFrame(() => resolve()));
    }
    // Statuses that get Supercluster'd (visible at every zoom)
    static ALWAYS_VISIBLE_STATUSES = ['Active', 'Pending'];
    // Statuses that only show at high zoom (never clustered - always individual)
    static HIGH_ZOOM_ONLY_STATUSES = ['Sold', 'Withdrawn', 'Expired', 'Cancelled'];
    // Render order for clusters/markers (first = behind, last = on top)
    // Off-market statuses render first (behind), then Pending, Active on top
    static STATUS_RENDER_ORDER = ['Withdrawn', 'Expired', 'Cancelled', 'Pending', 'Active', 'Sold'];
    /**
     * Get cached marker library (avoids repeated async imports)
     */
    async getMarkerLibrary() {
        if (!this.markerLibrary) {
            this.markerLibrary = await google.maps.importLibrary('marker');
        }
        return this.markerLibrary;
    }
    /**
     * Tells the map how much of its right edge is currently covered by UI
     * chrome (the UnifiedDrawer, when open) so fit-bounds calls - e.g.
     * enterNeighborhoodMode's - can fit content into the actually-visible map
     * area instead of the full viewport. Caller (main.ts) keeps this in sync
     * via the drawer's onOpen/onClose/onResize callbacks.
     */
    setRightInset(px) {
        this.rightInsetPx = Math.max(0, px);
    }
    /**
     * Set badge options (which attributes to show badges for)
     */
    setBadgeOptions(options) {
        this.badgeOptions = { ...this.badgeOptions, ...options };
        // Rebuild markers to show/hide badges
        if (this.state.map) {
            this.updateListingLayer(true);
            // Neighborhood badges previously only refreshed on the next pan/zoom
            // (whatever call happened to rebuild that neighborhood's content next)
            // - this was never wired to badgeOptions changes at all. Force past
            // showNeighborhoodMarkers's outer "nothing changed" gate (same trick
            // setListings uses) so the per-neighborhood signature - now badge-flag
            // aware, see computeNeighborhoodBadgeFlags - gets a chance to notice
            // and rebuild.
            this.lastNeighborhoodLabelsVisible = null;
            this.updateNeighborhoodMarkers();
        }
    }
    /**
     * Set listings to display on map. Active/Pending render through
     * Supercluster (see rebuildClusterIndexes); everything else renders
     * individually at its own zoom threshold.
     */
    setListings(listings) {
        if (!this.state.map)
            return;
        // No-op if this is the exact same SET (order-independent - the map's
        // visual output never depends on array order, only the drawer's does,
        // and that's a separate rendering path) already rendered. Sorted
        // rather than joined as-is because the backing query
        // (data-api's /listings/markers) has no ORDER BY, so two fetches of
        // identical underlying rows aren't guaranteed to come back in the same
        // order - an as-is join would have falsely detected those as
        // "changed" and done a full rebuild for zero visual difference.
        // Callers can legitimately hand back an equivalent array built fresh
        // each time (e.g. main.ts's filterAndSortListings), and multiple
        // independent triggers (init's explicit calls, the map's own first
        // idle-triggered viewport reload) used to each force a full
        // teardown+rebuild of every marker for data that hadn't actually
        // changed since the last render.
        const signature = listings.map(l => l.id).sort().join(',');
        if (signature === this.lastSetListingsSignature)
            return;
        this.lastSetListingsSignature = signature;
        // Deliberately NOT a full clearListings() here - individual listing
        // markers are diffed against the previous render inside
        // renderListingLayer (see upsertIndividualListingMarker), so a data
        // change where most listings are unchanged (typical: a re-sort, or a
        // refetch of the same viewport) reuses most existing markers instead
        // of destroying and recreating everything. Cluster bubbles have no
        // identity worth preserving across a data change, so those still
        // clear unconditionally.
        this.hideClusterMarkers();
        this.state.listings = listings.filter(l => l.lat && l.lng);
        this.clusterIndexes.clear();
        this.state.selectedListingId = null;
        // hoveredListingId intentionally left alone - if the hovered listing
        // is still present after this update, renderListingLayer's diff
        // re-applies the highlight to whichever marker now represents it
        // (new or reused) instead of it flickering off.
        // Invalidate caches (will be recalculated on next use)
        this.cachedListingsByNeighborhood = null;
        this.lastNeighborhoodLabelsVisible = null; // Force marker content update with new counts
        this.rebuildClusterIndexes();
        // Show appropriate layer - forced, since this is new data, not a zoom
        // change (indexes/listings just changed above).
        this.updateListingLayer(true);
        // Update neighborhood markers with new listing counts
        this.updateNeighborhoodMarkers();
        // If in neighborhood mode, refresh the property markers with new listings
        if (this.state.neighborhoodMode.active) {
            this.refreshNeighborhoodMarkers();
        }
    }
    /**
     * Set active statuses for NHM cache filtering
     * When in NHM with cached listings, this enables instant CNTL toggling without API calls
     */
    setActiveStatuses(statuses) {
        console.log(`[Map.setActiveStatuses] ========== STATUS CHANGE ==========`);
        console.log(`[Map.setActiveStatuses] New statuses:`, statuses);
        console.log(`[Map.setActiveStatuses] Previous statuses:`, Array.from(this.state.activeStatuses));
        this.state.activeStatuses = new Set(statuses);
        // Legacy badge layer's visibility depends on activeStatuses too now (see
        // updateVisibility) - re-evaluate immediately so unchecking "For Sale"
        // hides it right away instead of waiting for the next zoom/pan.
        this.updateVisibility();
        // Force neighborhood markers to rebuild content (count badges depend on
        // whether 'Active' is enabled — the lastNeighborhoodLabelsVisible
        // early-return optimization in showNeighborhoodMarkers only tracks zoom,
        // so a status toggle alone would leave stale badges otherwise).
        this.lastNeighborhoodLabelsVisible = null;
        // If in NHM with cached data, refresh markers immediately (no API call needed)
        const tmkCount = this.state.neighborhoodMode.tmkStatusMarkers.length;
        console.log(`[Map.setActiveStatuses] tmkStatusMarkers count: ${tmkCount}, NHM active: ${this.state.neighborhoodMode.active}`);
        const hasCache = tmkCount > 0;
        if (this.state.neighborhoodMode.active && hasCache) {
            console.log(`[Map.setActiveStatuses] Using cache - refreshing NHM markers (NO API CALL)`);
            this.refreshNeighborhoodMarkers();
        }
        // Always update neighborhood markers (bubbles) to reflect status changes
        console.log(`[Map.setActiveStatuses] Updating neighborhood markers for status change`);
        this.updateNeighborhoodMarkers();
        // Rebuild listing/cluster markers to reflect status changes - no
        // explicit hideListingMarkers() here, updateListingLayer's diff
        // reuses whichever individual markers are still relevant instead of
        // destroying every pin just because a different status got toggled.
        console.log(`[Map.setActiveStatuses] Rebuilding listing layer for status change`);
        this.hideClusterMarkers();
        this.updateListingLayer(true);
        console.log(`[Map.setActiveStatuses] ===================================`);
    }
    /**
     * Set property filters for NHM markers (price, beds, baths, type)
     * When in NHM with cached data, this refreshes markers with new filters applied
     */
    setPropertyFilters(filters) {
        console.log(`[Map.setPropertyFilters] New filters:`, filters);
        this.state.propertyFilters = filters;
        // If in NHM with cached data, refresh markers immediately
        const hasCache = this.state.neighborhoodMode.tmkStatusMarkers.length > 0;
        if (this.state.neighborhoodMode.active && hasCache) {
            console.log(`[Map.setPropertyFilters] Refreshing NHM markers with new filters`);
            this.refreshNeighborhoodMarkers();
        }
    }
    /**
     * Check if we're in NHM with cached data (for external status change optimization)
     */
    isUsingNeighborhoodCache() {
        const hasCache = this.state.neighborhoodMode.tmkStatusMarkers.length > 0;
        const result = this.state.neighborhoodMode.active && hasCache;
        console.log(`[Map.isUsingNeighborhoodCache] active=${this.state.neighborhoodMode.active}, tmkStatusMarkers=${this.state.neighborhoodMode.tmkStatusMarkers.length} → ${result}`);
        return result;
    }
    /** Converts listings into Supercluster's expected GeoJSON point input. */
    buildClusterPoints(listings) {
        const points = [];
        for (const listing of listings) {
            const lat = listing.unitLat ?? listing.lat;
            const lng = listing.unitLng ?? listing.lng;
            if (lat == null || lng == null || !Number.isFinite(lat) || !Number.isFinite(lng))
                continue;
            points.push({
                type: 'Feature',
                properties: { listingId: listing.id },
                geometry: { type: 'Point', coordinates: [lng, lat] },
            });
        }
        return points;
    }
    /**
     * Rebuilds the Supercluster indexes backing the Active/Pending listing
     * layer - two per status: "all" (every listing) and "unmapped" (only
     * ones not inside a currently-loaded neighborhood). updateListingLayer
     * picks whichever one to query based on zoom - below the neighborhood
     * marker crossover, "all" is used so every listing is represented; at/
     * above it, "unmapped" is used since in-neighborhood listings are picked
     * up by their neighborhood's own badge instead. Cheap to rebuild at this
     * dataset size; called whenever listings or neighborhoods change.
     */
    rebuildClusterIndexes() {
        this.clusterIndexes.clear();
        const visibleNeighborhoodIds = new Set(this.state.neighborhoods.map(n => n.id));
        const radius = this.clusterRadius;
        for (const status of SlowMauiMap.ALWAYS_VISIBLE_STATUSES) {
            const statusListings = this.state.listings.filter(l => l.status === status);
            const unmappedListings = statusListings.filter(l => !l.neighborhoodId || !visibleNeighborhoodIds.has(l.neighborhoodId));
            this.clusterIndexes.set(`${status}:all`, new Supercluster({
                radius,
                maxZoom: SlowMauiMap.CLUSTER_MAX_ZOOM,
            }).load(this.buildClusterPoints(statusListings)));
            this.clusterIndexes.set(`${status}:unmapped`, new Supercluster({
                radius,
                maxZoom: SlowMauiMap.CLUSTER_MAX_ZOOM,
            }).load(this.buildClusterPoints(unmappedListings)));
        }
    }
    /**
     * Update listing layer based on zoom level and neighborhood mode.
     *
     * Active/Pending render through Supercluster (see rebuildClusterIndexes)
     * - one continuous system that smoothly breaks clusters up into
     * individual pins as you zoom in, with no hard mode switch. Below the
     * neighborhood-marker crossover zoom (NEIGHBORHOOD_MARKERS_MIN_ZOOM),
     * every listing is included regardless of neighborhood membership; at/
     * above it, in-neighborhood listings drop out of these bubbles at the
     * same moment their neighborhood's own badge picks them up (the "all"
     * vs "unmapped" index variants).
     *
     * Sold/Withdrawn/Expired/Cancelled never cluster - always individual,
     * gated by SOLD_ZOOM_THRESHOLD.
     *
     * @param force - Skip the "did anything actually change" check below and
     * rebuild unconditionally. That check exists to avoid marker churn on
     * every zoom_changed tick during a pinch-zoom gesture (real perf need),
     * but the same optimization means a call triggered by a *data* change
     * (status filter, listings reloaded, badge options) rather than a zoom
     * change can silently no-op if the signature hasn't changed since the
     * last render - callers changing what should be on screen for a
     * non-zoom reason must pass true.
     */
    updateListingLayer(force = false) {
        if (!this.state.map)
            return;
        // Skip if in neighborhood mode (neighborhood mode shows its own markers)
        if (this.state.neighborhoodMode.active) {
            this.hideListingMarkers();
            this.hideClusterMarkers();
            return;
        }
        const zoom = this.state.map.getZoom() ?? DEFAULT_ZOOM;
        const showPrices = zoom >= SlowMauiMap.PRICE_LABEL_ZOOM_THRESHOLD;
        const showSold = zoom >= SlowMauiMap.SOLD_ZOOM_THRESHOLD;
        // Below the crossover, every listing renders here regardless of
        // neighborhood membership (fixes the old bug where in-neighborhood
        // listings were suppressed even below the zoom where badges exist to
        // represent them at all). At/above it, in-neighborhood listings are
        // dropped since the badge now covers them.
        const useUnmapped = this.state.neighborhoodsVisible && !this.state.badgesForceHidden && zoom >= NEIGHBORHOOD_MARKERS_MIN_ZOOM;
        // One signature covering everything that affects what gets rendered -
        // replaces the old scattered per-threshold crossing flags.
        const signature = `${Math.floor(zoom)}:${showPrices ? 1 : 0}:${showSold ? 1 : 0}:${useUnmapped ? 1 : 0}`;
        if (!force && signature === this.lastListingLayerSignature)
            return;
        this.lastListingLayerSignature = signature;
        // Individual listing markers are NOT cleared here - renderListingLayer
        // diffs the new target set against whatever's already on the map and
        // reuses markers that persist across the change (see
        // upsertIndividualListingMarker), which is what makes a zoom tick that
        // only shifts the cluster/pin boundary at the edges cheap instead of
        // destroying and recreating every pin on screen. Cluster bubbles have
        // no stable identity worth preserving across a zoom or data change, so
        // those still clear unconditionally.
        this.hideClusterMarkers();
        void this.renderListingLayer(zoom, showPrices, showSold, useUnmapped);
    }
    // Zoom threshold for showing price labels
    static PRICE_LABEL_ZOOM_THRESHOLD = 17;
    /** Withdrawn/Expired/Cancelled all map to the CNTL "OffMarket" toggle; everything else is its own status. */
    cntlStatusFor(status) {
        return ['Withdrawn', 'Expired', 'Cancelled'].includes(status) ? 'OffMarket' : status;
    }
    /** favoriteIds/alertIds/agentPickIds as the shared-types AttributeIdSets shape the registry's matchers expect. */
    get attributeIdSets() {
        return { favorites: this.badgeOptions.favoriteIds, alerts: this.badgeOptions.alertIds, agentPicks: this.badgeOptions.agentPickIds, featured: this.badgeOptions.featuredIds };
    }
    /**
     * Whether a listing (already reduced to facts) matches any currently-
     * active attribute filter (New / Open House / Favorites / Alerts / Price
     * Change) - these make a listing visible on the map even when its own
     * status isn't toggled on, same "Status OR Attribute" rule main.ts
     * already applies to the Listings Drawer. Lets someone turn For Sale off
     * and still see, say, New listings. Shape-agnostic - works from either
     * ListingSummary or TmkStatusMarker via their respective adapters (see
     * matchesActiveAttribute / tmkMarkerMatchesActiveAttribute below).
     */
    matchesAnyActiveAttribute(facts) {
        return ATTRIBUTE_REGISTRY.some((entry) => this.badgeOptions.show[entry.id] && entry.matches(facts, this.attributeIdSets));
    }
    matchesActiveAttribute(listing) {
        return this.matchesAnyActiveAttribute(factsFromListingSummary(listing));
    }
    /** Status OR Attribute - see matchesActiveAttribute. */
    isListingVisible(listing) {
        return this.state.activeStatuses.has(this.cntlStatusFor(listing.status)) || this.matchesActiveAttribute(listing);
    }
    /**
     * Renders the whole listing layer in one pass, preserving
     * STATUS_RENDER_ORDER for stacking. Sold/Withdrawn/Expired/Cancelled are
     * always individual markers (gated by showSold); Active/Pending come
     * from Supercluster and can be either a cluster bubble or an individual
     * pin per feature, per whichever index (useUnmapped) is queried.
     *
     * Visibility (isListingVisible) is checked per listing, not per status
     * bucket - a status the CNTL toggle has turned off can still render
     * individual listings that match an active attribute filter instead of
     * disappearing entirely. Supercluster indexes are built from every
     * listing of a status regardless of activeStatuses (see
     * rebuildClusterIndexes), so a cluster's leaves are filtered - and its
     * displayed count/position recomputed from just the visible ones - at
     * render time here.
     */
    async renderListingLayer(zoom, showPrices, showSold, useUnmapped) {
        if (!this.state.map)
            return;
        const renderId = ++this.listingLayerRenderId;
        const { AdvancedMarkerElement } = await this.getMarkerLibrary();
        // A newer render call superseded this one while we were awaiting the
        // marker library (only a real wait on the very first call ever - the
        // import is cached after that) - bail out without touching state so
        // this stale call can't clobber whatever the newer one already did.
        if (!this.state.map || renderId !== this.listingLayerRenderId)
            return;
        const listingsById = new Map(this.state.listings.map(l => [l.id, l]));
        const worldBbox = [-180, -85, 180, 85];
        // Cluster bubbles are created synchronously below (always far fewer
        // than individual pins, cheap) - individual listings are only
        // collected here, then upserted in a chunked loop after this pass so
        // a dense area breaking out of a cluster (potentially hundreds of
        // pins at once) doesn't block the main thread in one long task. See
        // the chunked loop below.
        const individualListings = [];
        for (const status of SlowMauiMap.STATUS_RENDER_ORDER) {
            if (SlowMauiMap.HIGH_ZOOM_ONLY_STATUSES.includes(status)) {
                if (!showSold)
                    continue;
                for (const listing of this.state.listings.filter(l => l.status === status)) {
                    if (this.isListingVisible(listing)) {
                        individualListings.push(listing);
                    }
                }
                continue;
            }
            // Active / Pending - Supercluster-driven
            const index = this.clusterIndexes.get(`${status}:${useUnmapped ? 'unmapped' : 'all'}`);
            if (!index)
                continue;
            const color = STATUS_COLORS[status] || CLUSTER_COLOR;
            const features = index.getClusters(worldBbox, zoom);
            for (const feature of features) {
                const [lng, lat] = feature.geometry.coordinates;
                const properties = feature.properties;
                if (properties.cluster) {
                    const clusterId = properties.cluster_id;
                    const leaves = index.getLeaves(clusterId, Infinity)
                        .map(f => listingsById.get(f.properties.listingId))
                        .filter((l) => !!l)
                        .filter(l => this.isListingVisible(l));
                    const count = leaves.length;
                    if (count === 0)
                        continue;
                    // Recompute center from the visible subset - the cluster's
                    // original position was the centroid of every listing in it,
                    // which can be off if most were filtered out above.
                    let center = { lat, lng };
                    if (count !== properties.point_count) {
                        // Only average listings with a finite lat/lng - a stray NaN/
                        // invalid geocode averaged into this would corrupt the whole
                        // cluster's center (classic trigger for the camera snapping to
                        // Google's whole-Earth view on setCenter). Fall back to the
                        // cluster's own original centroid if nothing survives.
                        const finiteLeaves = leaves.filter((l) => {
                            const llat = l.unitLat ?? l.lat;
                            const llng = l.unitLng ?? l.lng;
                            return Number.isFinite(llat) && Number.isFinite(llng);
                        });
                        if (finiteLeaves.length > 0) {
                            center = {
                                lat: finiteLeaves.reduce((sum, l) => sum + (l.unitLat ?? l.lat ?? 0), 0) / finiteLeaves.length,
                                lng: finiteLeaves.reduce((sum, l) => sum + (l.unitLng ?? l.lng ?? 0), 0) / finiteLeaves.length,
                            };
                        }
                    }
                    const content = this.createClusterContent(count, color, leaves);
                    const marker = new AdvancedMarkerElement({
                        position: center,
                        map: this.state.map,
                        content,
                        title: `${count} ${status.toLowerCase()} listings`,
                        zIndex: 50, // Lower than neighborhood markers (200)
                    });
                    // Click handler - zoom to exactly the level this cluster splits at.
                    content.addEventListener('click', () => {
                        this.state.map?.setCenter(center);
                        this.state.map?.setZoom(index.getClusterExpansionZoom(clusterId));
                    });
                    // Keys of every listing folded into this bubble - lets
                    // findListingMarker highlight the containing cluster when a
                    // hovered listing has been absorbed into one instead of getting
                    // its own dot (see that method).
                    marker.leafListingKeys = new Set(leaves.map((l) => listingItemKey(l)));
                    this.state.clusterMarkers.push(marker);
                }
                else {
                    const listing = listingsById.get(properties.listingId);
                    if (listing && this.isListingVisible(listing)) {
                        individualListings.push(listing);
                    }
                }
            }
        }
        // Diff individual markers against the previous render instead of
        // tearing every one down first - most survive unchanged across an
        // adjacent zoom tick or a status-toggle (only the small set at the
        // cluster/pin boundary actually changes), and reusing the existing
        // AdvancedMarkerElement (just swapping its .content) skips the
        // construction/map-registration cost of a brand new one. See
        // upsertIndividualListingMarker.
        const previousByKey = new Map();
        for (const marker of this.state.listingMarkers) {
            const listingData = marker.listingData;
            if (listingData)
                previousByKey.set(listingData.id, marker);
        }
        const nextMarkers = [];
        // Only markers newly created THIS pass (not reused ones) - tracked so
        // a supersession mid-chunk (below) can clean up markers this pass put
        // on the map but never got to register into state.listingMarkers.
        // Reused markers need no such cleanup: they're still referenced by the
        // (untouched, since we haven't reassigned it yet) old array, and
        // whichever newer render superseded this one will re-upsert or remove
        // them correctly on its own pass.
        const createdThisPass = [];
        const chunkSize = this.markerChunkSize;
        for (let i = 0; i < individualListings.length; i++) {
            const listing = individualListings[i];
            const existing = previousByKey.get(listing.id);
            previousByKey.delete(listing.id);
            const marker = this.upsertIndividualListingMarker(listing, showPrices, zoom, AdvancedMarkerElement, existing);
            nextMarkers.push(marker);
            if (!existing)
                createdThisPass.push(marker);
            if ((i + 1) % chunkSize === 0 && i + 1 < individualListings.length) {
                await this.yieldToFrame();
                if (renderId !== this.listingLayerRenderId || !this.state.map) {
                    // Superseded while yielded, or the map itself got torn down
                    // (e.g. component unmount) - bail without touching
                    // state.listingMarkers (a newer render's own diff runs against
                    // it unchanged, or there's no map left to matter); just remove
                    // the markers this aborted pass created that never made it into
                    // state, so they don't leak.
                    for (const marker of createdThisPass) {
                        marker.map = null;
                    }
                    return;
                }
            }
        }
        // Whatever's left in previousByKey wasn't part of this render (folded
        // into a cluster, filtered out, or gone from the dataset) - remove it.
        for (const stale of previousByKey.values()) {
            stale.map = null;
        }
        this.state.listingMarkers = nextMarkers;
    }
    /**
     * Creates a new individual listing marker, or - when this listing already
     * had one from the previous render pass (see renderListingLayer's diff) -
     * updates it in place instead: new content, repositioned, re-titled.
     * Reassigning .content is much cheaper than destroying and constructing a
     * fresh AdvancedMarkerElement (which re-registers with the map's marker
     * collision system), and avoids the flicker of a marker vanishing and
     * reappearing for a listing that didn't actually change identity - just
     * used both for high-zoom-only statuses (Sold, etc., never clustered) and
     * for Supercluster leaf points once a cluster has thinned out to one.
     */
    upsertIndividualListingMarker(listing, showPrices, zoom, AdvancedMarkerElement, existing) {
        const color = STATUS_COLORS[listing.status] || STATUS_COLORS.Active;
        const priceLabel = showPrices ? this.formatPriceLabel(listing.listPrice) : undefined;
        const content = this.createMarkerContent(color, false, priceLabel, zoom, listing);
        // Prefer unit-level coordinates (more precise) over TMK-level coordinates
        const markerLat = listing.unitLat ?? listing.lat;
        const markerLng = listing.unitLng ?? listing.lng;
        const title = `${this.formatPriceLabel(listing.listPrice)} - ${formatListingAddress(listing)}`;
        content.addEventListener('click', () => {
            this.selectListing(listing.id);
            if (this.options.onListingClick) {
                this.options.onListingClick(listing);
            }
        });
        if (existing) {
            existing.content = content;
            existing.position = { lat: markerLat, lng: markerLng };
            existing.title = title;
            existing.listingData = listing;
            // The old content node (possibly styled by setMarkerHighlighted) just
            // got replaced wholesale - re-apply the highlight to the new one if
            // this listing is the one currently hovered, otherwise it silently
            // drops instead of persisting through the marker update.
            if (this.state.hoveredListingId && listingItemKey(listing) === this.state.hoveredListingId) {
                this.setMarkerHighlighted(existing, true);
            }
            return existing;
        }
        const marker = new AdvancedMarkerElement({
            position: { lat: markerLat, lng: markerLng },
            map: this.state.map,
            content,
            title,
            zIndex: 50, // Lower than neighborhood markers (200) so neighborhoods are clickable
        });
        marker.listingData = listing;
        if (this.state.hoveredListingId && listingItemKey(listing) === this.state.hoveredListingId) {
            this.setMarkerHighlighted(marker, true);
        }
        return marker;
    }
    /**
     * Hide individual listing markers
     */
    hideListingMarkers() {
        for (const marker of this.state.listingMarkers) {
            marker.map = null;
        }
        this.state.listingMarkers = [];
    }
    /**
     * Hide cluster markers
     */
    hideClusterMarkers() {
        for (const marker of this.state.clusterMarkers) {
            marker.map = null;
        }
        this.state.clusterMarkers = [];
    }
    /**
     * Create marker content element (DOM-based for performance)
     * Shows price label at high zoom levels (>= 17)
     * Renders badges for New, Open House, Favorites based on badgeOptions
     */
    createMarkerContent(color, highlighted, priceLabel, zoom = DEFAULT_ZOOM, listing) {
        // Circle size based on zoom (v1 logic) - slightly larger for badge visibility
        let circleSize;
        let priceSize;
        if (zoom >= 19) {
            circleSize = 20;
            priceSize = 12;
        }
        else if (zoom >= 18) {
            circleSize = 18;
            priceSize = 11;
        }
        else if (zoom >= 17) {
            circleSize = 16;
            priceSize = 9;
        }
        else if (zoom >= 15) {
            circleSize = 14;
            priceSize = 0;
        }
        else {
            circleSize = highlighted ? 14 : 12;
            priceSize = 0;
        }
        // Container sized to circle only - price/badges positioned absolutely
        // This ensures the circle center is always at the lat/lng position
        const content = document.createElement('div');
        content.style.cssText = `
      position: relative;
      width: ${circleSize}px;
      height: ${circleSize}px;
      cursor: pointer;
    `;
        // Circle marker
        const circle = document.createElement('div');
        circle.style.cssText = `
      width: ${circleSize}px;
      height: ${circleSize}px;
      background: ${color};
      border-radius: 50%;
      border: 2px solid ${highlighted ? '#000' : '#fff'};
      box-shadow: 0 1px 3px rgba(0,0,0,0.3);
      transition: transform 120ms ease, box-shadow 120ms ease;
    `;
        content.appendChild(circle);
        // Price label positioned above circle (doesn't affect circle centering)
        if (priceLabel && priceSize > 0) {
            const price = document.createElement('div');
            price.style.cssText = `
        position: absolute;
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%);
        margin-bottom: 1px;
        font-size: ${priceSize}px;
        color: #555;
        text-shadow: 0 1px 1px rgba(255,255,255,0.8);
        white-space: nowrap;
      `;
            price.textContent = priceLabel;
            content.appendChild(price);
        }
        // Render badges (horizontal row below circle) if listing provided
        if (listing) {
            const badges = this.renderBadges(listing, circleSize);
            if (badges) {
                content.insertAdjacentHTML('beforeend', badges);
            }
        }
        return content;
    }
    /**
     * Which currently-active attributes (in registry/priority order) a
     * listing/cluster/marker matches, as ready-to-render {svg,color} entries.
     * `matches` decides per-attribute whether it applies (a single item's
     * facts, or a `.some()` across a group's facts - see call sites).
     */
    activeBadgeEntries(matches) {
        return ATTRIBUTE_REGISTRY
            .filter((entry) => this.badgeOptions.show[entry.id] && matches(entry))
            .map((entry) => ({ svg: `<svg viewBox="0 0 24 24" fill="currentColor">${entry.icon}</svg>`, color: entry.color }));
    }
    /** Renders a horizontal badge row positioned below the marker/cluster/badge anchor it's inserted into. */
    renderBadgeRow(activeBadges, badgeSize) {
        if (activeBadges.length === 0)
            return '';
        const badgeHtml = activeBadges
            .map(b => `<div style="width:${badgeSize}px;height:${badgeSize}px;display:flex;align-items:center;justify-content:center;color:${b.color};filter:drop-shadow(0 1px 1px rgba(0,0,0,0.3));">${b.svg}</div>`)
            .join('');
        return `<div style="position:absolute;top:100%;left:50%;transform:translateX(-50%);display:flex;gap:1px;margin-top:1px;pointer-events:none;">${badgeHtml}</div>`;
    }
    /**
     * Render badges for a listing as a horizontal row below the marker
     * Returns HTML string for badge container
     */
    renderBadges(listing, _markerSize) {
        // Badge size scales with marker size (like v1)
        const isDesktop = window.innerWidth >= 1024;
        const scale = isDesktop ? 1.5 : 1;
        const badgeSize = Math.round(BADGE_SIZE_BASE * scale);
        const facts = factsFromListingSummary(listing);
        const activeBadges = this.activeBadgeEntries((entry) => entry.matches(facts, this.attributeIdSets));
        return this.renderBadgeRow(activeBadges, badgeSize);
    }
    /**
     * Create cluster content element (DOM-based for performance)
     * Renders badges if any listing in the cluster has the attribute
     */
    createClusterContent(count, color = CLUSTER_COLOR, listings) {
        const size = Math.min(18 + Math.log2(count) * 5, 32);
        // Outer wrapper for relative positioning of badges
        const wrapper = document.createElement('div');
        wrapper.style.cssText = `
      position: relative;
      width: ${size}px;
      height: ${size}px;
    `;
        // Cluster bubble
        const content = document.createElement('div');
        content.style.cssText = `
      width: ${size}px;
      height: ${size}px;
      background: ${color};
      border-radius: 50%;
      border: 2px solid white;
      box-shadow: 0 1px 4px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: bold;
      font-size: ${Math.max(9, size / 3)}px;
      cursor: pointer;
    `;
        content.textContent = String(count);
        wrapper.appendChild(content);
        // Add badges if any listing in cluster has the attribute
        if (listings && listings.length > 0) {
            const badges = this.renderClusterBadges(listings, size);
            if (badges) {
                wrapper.insertAdjacentHTML('beforeend', badges);
            }
        }
        return wrapper;
    }
    /**
     * Render badges for a cluster as horizontal row below marker
     */
    renderClusterBadges(listings, _clusterSize) {
        // Badge size slightly larger for clusters
        const isDesktop = window.innerWidth >= 1024;
        const scale = isDesktop ? 1.5 : 1;
        const badgeSize = Math.round((BADGE_SIZE_BASE + 2) * scale);
        const factsList = listings.map(factsFromListingSummary);
        const activeBadges = this.activeBadgeEntries((entry) => factsList.some((f) => entry.matches(f, this.attributeIdSets)));
        return this.renderBadgeRow(activeBadges, badgeSize);
    }
    formatPriceLabel(price) {
        if (price >= 1000000) {
            return `$${(price / 1000000).toFixed(1)}M`;
        }
        return `$${Math.round(price / 1000)}K`;
    }
    /**
     * Highlight a listing marker (from ListingsDrawer card hover) - applies
     * directly to the existing marker's content DOM node (no
     * teardown/re-render) so it's cheap enough to call on every mouseenter/
     * mouseleave. If the listing is currently absorbed into a cluster bubble
     * rather than shown as its own dot, highlights the containing cluster
     * instead (see findListingMarker) - only a genuinely off-screen/filtered-
     * out listing no-ops.
     *
     * `key` must be a listingItemKey()-style value (tmkNormalized, falling
     * back to the internal id) - NOT necessarily ListingSummary.id verbatim.
     * Confirmed live: /listings/map/markers (the drawer's source outside NHM)
     * deliberately returns `id: tmkNormalized ?? id` "for consistency", so a
     * raw ListingSummary.id from the drawer doesn't reliably match a marker's
     * own identity across every source. TMK is the one identity every
     * marker-producing path actually shares - see findListingMarker.
     */
    highlightListing(key) {
        const previousKey = this.state.hoveredListingId;
        if (previousKey === key)
            return;
        if (previousKey) {
            const previousMarker = this.findListingMarker(previousKey);
            if (previousMarker)
                this.setMarkerHighlighted(previousMarker, false);
        }
        this.state.hoveredListingId = key;
        if (key) {
            const marker = this.findListingMarker(key);
            if (marker)
                this.setMarkerHighlighted(marker, true);
        }
    }
    /**
     * Finds a listing's currently-rendered marker by TMK - its own individual
     * dot if it has one, otherwise the cluster bubble it's currently folded
     * into (Neighborhood Mode never clusters, so that fallback only applies
     * to the default city-wide view). Checks, in order: state.listingMarkers
     * (via listingItemKey(listingData)), state.neighborhoodMode.propertyMarkers
     * (via tmkStatusMarkerData.tmk - TmkStatusMarker has no .id at all, and
     * its .listingId is the public MLS# rather than an identity that matches
     * anything else here), then state.clusterMarkers (via the leafListingKeys
     * set stashed on each cluster at creation - see renderListingLayer).
     * Returns null only if the listing is genuinely off-screen/filtered out.
     */
    findListingMarker(key) {
        for (const marker of this.state.listingMarkers) {
            const listingData = marker.listingData;
            if (listingData && listingItemKey(listingData) === key)
                return marker;
        }
        for (const marker of this.state.neighborhoodMode.propertyMarkers) {
            if (marker.tmkStatusMarkerData?.tmk === key)
                return marker;
        }
        for (const marker of this.state.clusterMarkers) {
            const leafKeys = marker.leafListingKeys;
            if (leafKeys?.has(key))
                return marker;
        }
        return null;
    }
    /**
     * Toggles the hover treatment directly on a marker's content node - a
     * white halo + dark outer ring plus a modest scale-up, applied to the
     * circle child only (not the content wrapper) so the marker's
     * lat/lng-anchored bounding box never changes size/position.
     * createMarkerContent, createTmkStatusMarkerContent, and
     * createClusterContent all build the same shape (a sized wrapper div
     * whose first child is the color circle/bubble), so this one
     * implementation covers all three marker systems - including a cluster
     * bubble standing in for a listing folded into it (see
     * findListingMarker).
     */
    setMarkerHighlighted(marker, highlighted) {
        const circle = marker.content?.firstElementChild;
        if (!circle)
            return;
        if (highlighted) {
            // Stash the pre-hover zIndex/shadow (not hardcoded - dimmed NHM
            // markers use a different base shadow than regular ones) so
            // un-highlighting restores exactly what was there before, whichever
            // builder created this marker.
            marker._baseZIndex = marker.zIndex;
            marker._baseBoxShadow = circle.style.boxShadow;
            marker.zIndex = 1000;
            circle.style.transform = 'scale(1.3)';
            circle.style.boxShadow = '0 0 0 3px #fff, 0 0 0 6px rgba(0,0,0,0.35), 0 2px 8px rgba(0,0,0,0.45)';
        }
        else {
            if (marker._baseZIndex !== undefined) {
                marker.zIndex = marker._baseZIndex;
            }
            circle.style.transform = '';
            if (marker._baseBoxShadow !== undefined) {
                circle.style.boxShadow = marker._baseBoxShadow;
            }
        }
    }
    /**
     * Select a listing (from click)
     */
    selectListing(id) {
        this.state.selectedListingId = id;
    }
    clearListings() {
        this.hideListingMarkers();
        this.hideClusterMarkers();
        this.state.listings = [];
        this.clusterIndexes.clear();
        this.state.selectedListingId = null;
        this.state.hoveredListingId = null;
    }
    /**
     * Get current viewport bounds
     */
    getBounds() {
        if (!this.state.map)
            return null;
        const bounds = this.state.map.getBounds();
        if (!bounds)
            return null;
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        return {
            north: ne.lat(),
            south: sw.lat(),
            east: ne.lng(),
            west: sw.lng(),
        };
    }
    handleBoundsChanged() {
        const bounds = this.getBounds();
        if (bounds && this.options.onBoundsChanged) {
            this.options.onBoundsChanged(bounds);
        }
        // Building extrusion is drawn in screen-space pixels, so it has to be
        // recomputed whenever the map settles after a pan/zoom. Piggybacking on
        // this handler (rather than a standalone 'idle' listener) because
        // enterNeighborhoodMode() temporarily swaps out ALL 'idle' listeners via
        // google.maps.event.clearListeners() during its fitBounds sequence — a
        // separately-registered listener would get silently dropped the first
        // time NHM entry runs. This handler is always the one re-registered.
        this.redrawBuildingOverlays();
    }
    // ==================== NEIGHBORHOODS ====================
    setNeighborhoods(neighborhoods) {
        if (!this.state.map)
            return;
        // Clear polygons/legacy pins only - NOT the neighborhood-count bubbles,
        // which diff themselves against the previous call in
        // updateNeighborhoodMarkers() below. See
        // clearNeighborhoodPolygonsAndLegacyPins's comment for why this
        // matters: this method runs twice on every page load (lightweight
        // pass, then the full-polygon upgrade), and a full clear here used to
        // make every bubble flash on the second call for no visual reason.
        this.clearNeighborhoodPolygonsAndLegacyPins();
        // Store neighborhoods for later reference
        this.state.neighborhoods = neighborhoods;
        this.districtHoleRingsCache = null;
        // The "unmapped" cluster index variant depends on which neighborhoods
        // are loaded - rebuild in case this ran after setListings.
        this.rebuildClusterIndexes();
        // Add polygons for each neighborhood
        for (const neighborhood of neighborhoods) {
            this.addNeighborhood(neighborhood);
        }
        this.updateVisibility();
        this.updateNeighborhoodMarkers();
        this.updateListingLayer(true);
    }
    /**
     * Set neighborhoods visibility based on toggle
     * Note: Polygons remain hidden until entering neighborhood mode
     */
    setNeighborhoodsVisible(visible) {
        this.state.neighborhoodsVisible = visible;
        // Polygons stay hidden - they only show in neighborhood mode
        // (No polygon visibility toggle here)
        // Update neighborhood markers
        this.updateNeighborhoodMarkers();
        // Exit neighborhood mode if turning off
        if (!visible && this.state.neighborhoodMode.active) {
            this.exitNeighborhoodMode();
        }
        // Rebuild listing layer to filter/unfilter in-neighborhood listings
        this.hideListingMarkers();
        this.hideClusterMarkers();
        this.updateListingLayer(true);
    }
    /**
     * Force-hides (or restores) the legacy per-neighborhood for-sale-count
     * badges independent of zoom/neighborhoodsVisible. These are a separate,
     * always-loaded layer (populated once from each neighborhood's static
     * forSaleCount at setNeighborhoods() time, unrelated to setListings()) -
     * visually near-identical to real "Active" markers (same green circle,
     * white border, numeric count), so a caller showing an explicit, exact
     * set of listings (e.g. a fixed search result) needs a way to suppress
     * them regardless of what zoom level the camera lands on.
     */
    setNeighborhoodBadgesVisible(visible) {
        this.state.badgesForceHidden = !visible;
        this.updateVisibility();
    }
    /**
     * Set zoning display mode
     * When enabled, neighborhood polygons are shown and filled with zoning-based colors
     * When disabled, polygons are hidden (unless in neighborhood mode)
     */
    /**
     * Whether a neighborhood's regular polygon should be visible right now -
     * the single source of truth every mutator below defers to instead of
     * deciding independently. That "each decides independently" pattern is
     * exactly how two real bugs happened: exiting NHM only ever restored
     * Zoning (never learned about Market Trends when it was added later),
     * and hover's mouseout had the same gap until getNeighborhoodBaseStyle()
     * unified the *style* half of this - this unifies the *visibility* half
     * the same way, and adds the Outline toggle as a first-class input.
     */
    computeNeighborhoodPolygonVisible(neighborhood) {
        const isActiveNHM = this.state.neighborhoodMode.active &&
            this.state.neighborhoodMode.neighborhoodId === neighborhood.id;
        if (isActiveNHM)
            return false; // NHM draws its own dashed border instead
        if (this.state.neighborhoodFillMode === 'zoning') {
            const zoning = normalizeZoningCategory(neighborhood.zoning);
            return this.state.zoningCategories[zoning] !== false;
        }
        if (this.state.neighborhoodFillMode === 'marketTrends' || this.state.neighborhoodFillMode === 'climate') {
            return true;
        }
        // No fill active - Outline is the only thing that can show it.
        return this.state.neighborhoodOutlineEnabled;
    }
    /** Applies the correct style + visibility to one neighborhood's polygon, given current state. */
    applyNeighborhoodPolygonState(neighborhood) {
        const polygon = this.state.polygons.get(neighborhood.id);
        if (!polygon)
            return;
        polygon.setOptions(this.getNeighborhoodBaseStyle(neighborhood));
        polygon.setVisible(this.computeNeighborhoodPolygonVisible(neighborhood));
    }
    applyAllNeighborhoodPolygonStates() {
        for (const neighborhood of this.state.neighborhoods) {
            this.applyNeighborhoodPolygonState(neighborhood);
        }
    }
    /**
     * Display tab "Neighborhood" > Outline - a plain bordered polygon with no
     * fill, independent of Labels and of whatever fill mode is active.
     */
    setNeighborhoodOutlineEnabled(enabled) {
        this.state.neighborhoodOutlineEnabled = enabled;
        this.applyAllNeighborhoodPolygonStates();
    }
    isNeighborhoodOutlineEnabled() {
        return this.state.neighborhoodOutlineEnabled;
    }
    /** Kept for any direct caller - routes through setNeighborhoodFillMode so there's one path, not two. */
    setZoningEnabled(enabled) {
        this.setNeighborhoodFillMode(enabled ? 'zoning' : 'none');
    }
    /**
     * Check if zoning display is enabled
     */
    isZoningEnabled() {
        return this.state.zoningEnabled;
    }
    /**
     * Feeds the Display tab's "Market Trends" data in (from
     * GET /stats/price-heatmap) - stores both selectable metrics, then
     * refreshes whichever one is currently active (min/max/center + re-applies
     * both fill layers - see refreshActivePriceMetricData) so new data shows
     * immediately regardless of which fill mode (if any) is currently active.
     */
    setPriceHeatmapData(byMetric) {
        for (const metric of Object.keys(byMetric)) {
            const data = byMetric[metric];
            this.state.priceMetricData[metric] = {
                neighborhoods: new Map(Object.entries(data.neighborhoods)),
                districts: new Map(Object.entries(data.districts)),
            };
        }
        this.refreshActivePriceMetricData();
    }
    /**
     * Switches which metric the Market Trends fill/legend reflect. Same
     * underlying color logic (getMarketTrendsColor) for both - only the
     * data and its min/center/max change.
     */
    setPriceMetric(metric) {
        if (this.state.activePriceMetric === metric)
            return;
        this.state.activePriceMetric = metric;
        this.refreshActivePriceMetricData();
    }
    getPriceMetric() {
        return this.state.activePriceMetric;
    }
    // Copies the active metric's stored data into the *PriceData/*PriceRange
    // fields that the fill-rendering code actually reads, then re-applies both
    // layers unconditionally (cheap - a few hundred neighborhoods/districts).
    refreshActivePriceMetricData() {
        const active = this.state.priceMetricData[this.state.activePriceMetric];
        this.state.neighborhoodPriceData = active.neighborhoods;
        this.state.districtPriceData = active.districts;
        this.state.neighborhoodPriceRange = this.computePriceRange(this.state.neighborhoodPriceData);
        this.state.districtPriceRange = this.computePriceRange(this.state.districtPriceData);
        this.applyAllNeighborhoodPolygonStates();
        this.applyDistrictFill();
    }
    getNeighborhoodPriceRange() {
        return { ...this.state.neighborhoodPriceRange };
    }
    getDistrictPriceRange() {
        return { ...this.state.districtPriceRange };
    }
    // `center` is the average of the values being colored, not the linear
    // midpoint of min/max - see getMarketTrendsColor's comment for why.
    computePriceRange(data) {
        const medians = Array.from(data.values())
            .filter((entry) => entry.sampleSize >= MARKET_TRENDS_MIN_SAMPLE_SIZE)
            .map((entry) => entry.median);
        if (medians.length === 0)
            return { min: 0, center: 0, max: 0 };
        const center = medians.reduce((sum, v) => sum + v, 0) / medians.length;
        const sorted = [...medians].sort((a, b) => a - b);
        // 95th percentile, not the literal max - one thin, extreme-priced area
        // (e.g. 3 ultra-luxury listings) would otherwise stretch `max` far
        // enough to wash every genuinely-expensive-but-not-extreme neighborhood
        // down into yellow/green. getMarketTrendsColor clamps values above this
        // to the ramp's darkest red rather than erroring or extrapolating, so
        // capping here just means "very expensive" areas share the top color
        // instead of each getting their own shade.
        const max = sorted[Math.floor(0.95 * (sorted.length - 1))];
        return { min: Math.min(...medians), center, max };
    }
    /**
     * Feeds the Display tab's Climate overlay data in (from GET /climate,
     * called once per datatype) - stores whichever datatype was just loaded,
     * then refreshes if it's the currently-active one. Mirrors
     * setPriceHeatmapData/setPriceMetric's split for the same reason: rainfall
     * and temperature are fetched/cached independently (apps/public loads one
     * at a time, on demand) but share one active-data/range pair the fill
     * rendering actually reads.
     */
    setClimateHeatmapData(datatype, data) {
        this.state.climateDatatypeData[datatype] = {
            neighborhoods: new Map(Object.entries(data.neighborhoods)),
            districts: new Map(Object.entries(data.districts)),
        };
        if (this.state.activeClimateDatatype === datatype) {
            this.refreshActiveClimateData();
        }
    }
    setClimateDatatype(datatype) {
        if (this.state.activeClimateDatatype === datatype)
            return;
        this.state.activeClimateDatatype = datatype;
        this.refreshActiveClimateData();
    }
    getClimateDatatype() {
        return this.state.activeClimateDatatype;
    }
    refreshActiveClimateData() {
        const active = this.state.climateDatatypeData[this.state.activeClimateDatatype];
        this.state.neighborhoodClimateData = active.neighborhoods;
        this.state.districtClimateData = active.districts;
        this.state.neighborhoodClimateRange = this.computeClimateRange(this.state.neighborhoodClimateData);
        this.state.districtClimateRange = this.computeClimateRange(this.state.districtClimateData);
        this.applyAllNeighborhoodPolygonStates();
        this.applyDistrictFill();
    }
    getNeighborhoodClimateRange() {
        return { ...this.state.neighborhoodClimateRange };
    }
    getDistrictClimateRange() {
        return { ...this.state.districtClimateRange };
    }
    // Same 95th-percentile max-clamp reasoning as computePriceRange - Maui's
    // rainfall in particular can have a few genuinely extreme windward-valley
    // values that would otherwise wash out every other neighborhood's color.
    // No sample-size filter here (unlike price) - an entity either has a
    // value (one already-aggregated 12-month number) or it's simply absent.
    computeClimateRange(data) {
        const values = Array.from(data.values()).map((entry) => entry.value);
        if (values.length === 0)
            return { min: 0, center: 0, max: 0 };
        const center = values.reduce((sum, v) => sum + v, 0) / values.length;
        const sorted = [...values].sort((a, b) => a - b);
        const max = sorted[Math.floor(0.95 * (sorted.length - 1))];
        return { min: Math.min(...values), center, max };
    }
    /**
     * Display tab "Neighborhood" fill mode - None / Zoning / Market Trends.
     * Mutually exclusive (both fill the same polygon layer); the DisplayDrawer
     * UI already enforces this, this keeps Map.ts correct even if ever called
     * out of step with that.
     */
    setNeighborhoodFillMode(mode) {
        this.state.neighborhoodFillMode = mode;
        this.state.zoningEnabled = mode === 'zoning'; // kept in sync for isZoningEnabled()
        this.applyAllNeighborhoodPolygonStates();
    }
    getNeighborhoodFillMode() {
        return this.state.neighborhoodFillMode;
    }
    /**
     * Display tab "District" fill mode - None / Market Trends. Only these two
     * (no Zoning at district grain), so no segmented-control backstop needed
     * the way neighborhood fill has.
     */
    setDistrictFillMode(mode) {
        this.state.districtFillMode = mode;
        this.applyDistrictFill();
    }
    getDistrictFillMode() {
        return this.state.districtFillMode;
    }
    /**
     * Re-applies district fill styling from current state. Takes no
     * parameter - always reads this.state.districtFillMode - since it's
     * called both when that mode changes and when new price data arrives
     * while it's already on (setPriceHeatmapData).
     *
     * The fill polygon's own visibility is forced on whenever fill is active,
     * regardless of the separate Outline toggle (districtOutlineEnabled) -
     * same relationship as neighborhoods, where a fill mode always shows the
     * polygon even with Outline off. Without this, "Trends on, Outline off"
     * would render nothing at all, since the fill polygon's visibility used
     * to be set only by the outline toggle at creation time and never
     * revisited.
     */
    applyDistrictFill() {
        const mode = this.state.districtFillMode;
        for (const district of this.state.districts) {
            const polygon = this.state.districtPolygons.get(district.id);
            if (!polygon)
                continue;
            if (mode === 'none') {
                polygon.setOptions({ fillOpacity: 0, strokeOpacity: 0 });
                polygon.setVisible(this.state.districtOutlineEnabled);
                continue;
            }
            let color;
            let fillOpacity;
            if (mode === 'marketTrends') {
                const entry = this.state.districtPriceData.get(district.id);
                const { min, center, max } = this.state.districtPriceRange;
                color = entry && entry.sampleSize >= MARKET_TRENDS_MIN_SAMPLE_SIZE
                    ? getMarketTrendsColor(entry.median, min, center, max)
                    : MARKET_TRENDS_NO_DATA_COLOR;
                fillOpacity = MARKET_TRENDS_FILL_OPACITY;
            }
            else {
                const entry = this.state.districtClimateData.get(district.id);
                const { min, center, max } = this.state.districtClimateRange;
                const ramp = CLIMATE_RAMPS[this.state.activeClimateDatatype];
                color = entry ? getClimateColor(ramp, entry.value, min, center, max) : CLIMATE_NO_DATA_COLOR;
                fillOpacity = CLIMATE_FILL_OPACITY;
            }
            // Punch every contained neighborhood out of the district's own fill -
            // Overlay colors both layers at once, so without this the district's
            // semi-transparent fill stacks underneath each neighborhood's own
            // fill and visibly darkens/muddies it (two 55%-opacity layers instead
            // of one). See getDistrictHoleRings.
            const outerRing = district.polygon.map((p) => ({ lat: p.lat, lng: p.lng }));
            polygon.setPaths([outerRing, ...this.getDistrictHoleRings(district)]);
            // Solid border in the fill's own color (unlike the dashed grey
            // Outline layer) - a filled district reads better with some edge
            // definition, same as neighborhoods get in Zoning/Trends mode.
            polygon.setOptions({ fillColor: color, fillOpacity, strokeColor: color, strokeOpacity: 0.8, strokeWeight: 1 });
            polygon.setVisible(true);
        }
    }
    /**
     * Neighborhoods that sit inside a given district, as hole rings ready to
     * punch into that district's fill polygon (see applyDistrictFill).
     * Membership is centroid-based (a neighborhood's center point tested
     * against the district's boundary via ray casting) rather than exact
     * polygon intersection - cheap, and correct for how this data is
     * actually drawn (neighborhoods don't straddle district boundaries).
     * Computed once per district and cached, since the geometry never
     * changes after load; invalidated in setDistricts/setNeighborhoods.
     */
    getDistrictHoleRings(district) {
        if (!this.districtHoleRingsCache) {
            this.districtHoleRingsCache = new Map();
        }
        const cached = this.districtHoleRingsCache.get(district.id);
        if (cached)
            return cached;
        const outerRing = district.polygon.map((p) => ({ lat: p.lat, lng: p.lng }));
        const holes = [];
        for (const neighborhood of this.state.neighborhoods) {
            if (!neighborhood.polygon || neighborhood.polygon.length < 3)
                continue;
            if (!pointInRing({ lat: neighborhood.centerLat, lng: neighborhood.centerLng }, outerRing))
                continue;
            const ring = neighborhood.polygon.map((p) => ({ lat: p.lat, lng: p.lng }));
            holes.push(asOppositeWinding(outerRing, ring));
        }
        this.districtHoleRingsCache.set(district.id, holes);
        return holes;
    }
    /**
     * Toggle the fake-3D building extrusion effect in neighborhood mode.
     * Default off; safe to flip at any time, including while a neighborhood
     * is already open (redraws immediately).
     */
    setBuildingExtrusionEnabled(enabled) {
        this.state.buildingExtrusionEnabled = enabled;
        this.redrawBuildingOverlays();
    }
    isBuildingExtrusionEnabled() {
        return this.state.buildingExtrusionEnabled;
    }
    /**
     * Experimental: show unit numbers directly on off-market NHM markers
     * (faded circle + unit text) instead of a plain gray dot. Gated behind
     * a feature flag (see FEATURE_FLAGS.offMarketUnitLabels in main.ts) so
     * it can be reverted instantly without touching this file. Safe to flip
     * at any time, including while a neighborhood is already open (redraws
     * immediately).
     */
    setOffMarketUnitLabelsEnabled(enabled) {
        this.state.offMarketUnitLabelsEnabled = enabled;
        if (this.state.neighborhoodMode.active) {
            void this.refreshNeighborhoodMarkers();
        }
    }
    isOffMarketUnitLabelsEnabled() {
        return this.state.offMarketUnitLabelsEnabled;
    }
    /**
     * Set visibility of a specific zoning category
     */
    setZoningCategoryVisible(category, visible) {
        this.state.zoningCategories[category] = visible;
        if (this.state.neighborhoodFillMode === 'zoning') {
            for (const neighborhood of this.state.neighborhoods) {
                if (normalizeZoningCategory(neighborhood.zoning) === category) {
                    this.applyNeighborhoodPolygonState(neighborhood);
                }
            }
        }
    }
    /**
     * Neighborhood ids directly favorited/alerted via the `nh:${id}` id-set
     * convention (not any individual listing inside them) and whose
     * attribute is currently toggled on. Used to force those neighborhoods'
     * markers to render below the normal zoom crossover, since - unlike a
     * favorited/alerted listing, which already shows via its own property
     * bubble at every zoom - a directly-favorited neighborhood has nothing
     * else on the map to represent it until neighborhood markers turn on.
     */
    directlyAttributedNeighborhoodIds() {
        const ids = new Set();
        for (const entry of ATTRIBUTE_REGISTRY) {
            if (entry.kind !== 'id-set' || !this.badgeOptions.show[entry.id])
                continue;
            for (const key of this.attributeIdSets[entry.id] ?? []) {
                if (key.startsWith('nh:'))
                    ids.add(key.substring(3));
            }
        }
        return ids;
    }
    /**
     * Update neighborhood markers based on zoom level
     */
    async updateNeighborhoodMarkers() {
        if (!this.state.map)
            return;
        if (!this.state.neighborhoodsVisible) {
            this.hideNeighborhoodMarkers();
            return;
        }
        const zoom = this.state.map.getZoom() ?? DEFAULT_ZOOM;
        const showAllMarkers = zoom >= NEIGHBORHOOD_MARKERS_MIN_ZOOM;
        const showLabels = zoom >= NEIGHBORHOOD_LABELS_ZOOM;
        const forceIconOnlyIds = showAllMarkers ? new Set() : this.directlyAttributedNeighborhoodIds();
        if (!showAllMarkers && forceIconOnlyIds.size === 0) {
            this.hideNeighborhoodMarkers();
            return;
        }
        // Show/update markers
        await this.showNeighborhoodMarkers(showLabels, showAllMarkers, forceIconOnlyIds);
    }
    /**
     * Show neighborhood markers. Below the normal zoom crossover
     * (showAllMarkers false), only neighborhoods in forceIconOnlyIds render
     * at all, and only in minimal icon-only form (badge, no name/count/fill)
     * - see directlyAttributedNeighborhoodIds.
     */
    async showNeighborhoodMarkers(showLabels, showAllMarkers, forceIconOnlyIds) {
        if (!this.state.map)
            return;
        // Early exit if in neighborhood mode - no need to loop through neighborhoods
        if (this.state.neighborhoodMode.active) {
            this.hideNeighborhoodMarkers();
            return;
        }
        // Skip update if label visibility hasn't changed and markers already exist.
        // Only applies to the normal all-markers case - the icon-only override
        // touches a different, much smaller subset each time (whichever
        // neighborhoods are favorited/alerted), so it always runs the full
        // per-neighborhood pass below instead.
        if (showAllMarkers && this.lastNeighborhoodLabelsVisible === showLabels && this.state.neighborhoodMarkers.size > 0) {
            // Just ensure markers are visible
            for (const marker of this.state.neighborhoodMarkers.values()) {
                if (marker.map !== this.state.map) {
                    marker.map = this.state.map;
                }
            }
            return;
        }
        // null (rather than showLabels) whenever we're not in the normal
        // all-markers case, so the next call that IS in that case can't find a
        // stale match here and wrongly take the fast path above while some
        // markers are still sitting in icon-only form or hidden.
        this.lastNeighborhoodLabelsVisible = showAllMarkers ? showLabels : null;
        // Get cached marker library
        const { AdvancedMarkerElement } = await this.getMarkerLibrary();
        // Use cached listings by neighborhood if available
        if (!this.cachedListingsByNeighborhood) {
            this.cachedListingsByNeighborhood = this.getListingsByNeighborhood();
        }
        const listingsByNeighborhood = this.cachedListingsByNeighborhood;
        // Viewport bounds for skipping the expensive per-marker DOM rebuild
        // below on neighborhoods that aren't anywhere near what's on screen.
        // Google Maps already only paints markers actually within the visible
        // viewport - the cost here was never "too many markers", it was
        // rebuilding every neighborhood's label+badge content (2-3 DOM
        // elements with inline cssText each) on every call, all ~100+ of them,
        // even when only a couple are actually visible. An off-screen marker's
        // existing content just stays as-is (harmless - nobody's looking at
        // it) until a later pan brings it back into view and refreshes it then.
        const viewportBounds = this.state.map.getBounds();
        for (const neighborhood of this.state.neighborhoods) {
            // Calculate center position - use centerLat/centerLng if valid, otherwise calculate from polygon
            let centerLat = neighborhood.centerLat;
            let centerLng = neighborhood.centerLng;
            if ((!centerLat || !centerLng || (centerLat === 0 && centerLng === 0)) && neighborhood.polygon?.length) {
                // Calculate centroid from polygon
                const sumLat = neighborhood.polygon.reduce((sum, p) => sum + p.lat, 0);
                const sumLng = neighborhood.polygon.reduce((sum, p) => sum + p.lng, 0);
                centerLat = sumLat / neighborhood.polygon.length;
                centerLng = sumLng / neighborhood.polygon.length;
            }
            // Skip if no valid position
            if (!centerLat || !centerLng) {
                continue;
            }
            const existingMarker = this.state.neighborhoodMarkers.get(neighborhood.id);
            const iconOnly = !showAllMarkers && forceIconOnlyIds.has(neighborhood.id);
            const shouldShow = showAllMarkers || iconOnly;
            if (!shouldShow) {
                // Below the crossover and not itself directly favorited/alerted -
                // hide (not just "leave alone"), since forceIconOnlyIds can shrink
                // between calls as favorites/alerts change.
                if (existingMarker) {
                    existingMarker.map = null;
                    this.lastNeighborhoodMarkerSignature.delete(neighborhood.id);
                }
                continue;
            }
            // Already on the map (in its current form) and not currently visible -
            // leave its content alone rather than paying the rebuild cost for
            // something off-screen. Guarded on .map already matching so a marker
            // that's currently hidden (map === null, e.g. iconOnly just turned on
            // for it) always goes through the full rebuild below instead.
            if (existingMarker && existingMarker.map === this.state.map && viewportBounds && !viewportBounds.contains({ lat: centerLat, lng: centerLng })) {
                continue;
            }
            const neighborhoodListings = listingsByNeighborhood.get(neighborhood.id) || [];
            // Count from neighborhoodListings (state.listings, already filtered by
            // the caller's price/beds/baths/type/status selections via
            // setListings()) rather than the API's static forSaleCount — that
            // field is a raw tmk_status total and ignores every client-side
            // filter, which used to make the badge show the neighborhood's total
            // for-sale count even when e.g. a price filter was narrowing results.
            // Icon-only markers never show a count - there's no "for sale" chrome
            // to attach one to, just the attribute badge itself.
            const forSaleActive = this.state.activeStatuses.has('Active');
            const visibleCount = !iconOnly && forSaleActive ? neighborhoodListings.filter(l => l.status === 'Active').length : 0;
            // Badge flags folded into the signature too - previously only
            // showLabels/visibleCount were tracked, so a badgeOptions change
            // (toggling New/Open House/Favorites/Alerts, or a favorite/alert
            // changing) never invalidated an on-screen neighborhood whose
            // for-sale count hadn't moved, and the stale badges only got fixed
            // incidentally by the next pan/zoom that happened to rebuild it.
            const badgeFlags = this.computeNeighborhoodBadgeFlags(neighborhoodListings, neighborhood.id);
            // On-screen but nothing about this neighborhood's rendered state has
            // actually changed since last time (e.g. a pan that didn't cross into
            // new listings territory) - skip the DOM rebuild here too. iconOnly is
            // folded in (as a distinct prefix, not just reusing showLabels) so a
            // marker transitioning between icon-only and normal form always
            // rebuilds even if showLabels/visibleCount/badgeSignature all match.
            const badgeSignature = ATTRIBUTE_REGISTRY.map((entry) => (badgeFlags[entry.id] ? 1 : 0)).join('');
            const signature = `${iconOnly ? 'icon' : showLabels ? 1 : 0}:${visibleCount}:${badgeSignature}`;
            if (existingMarker && existingMarker.map === this.state.map && this.lastNeighborhoodMarkerSignature.get(neighborhood.id) === signature) {
                continue;
            }
            this.lastNeighborhoodMarkerSignature.set(neighborhood.id, signature);
            // Create or update marker content
            const content = this.createNeighborhoodMarkerContent(neighborhood, showLabels, visibleCount, neighborhoodListings, iconOnly);
            // Always attach click handler to new content
            content.addEventListener('click', () => {
                if (this.options.onNeighborhoodClick) {
                    this.options.onNeighborhoodClick(neighborhood);
                }
            });
            // Base is comfortably above every other marker layer (listings 50,
            // districts/regions 120/150); visibleCount on top of that means a
            // busy neighborhood's label wins the paint order over a quiet
            // neighbor it overlaps with, instead of whichever happened to be
            // added to the map last - the same for-sale count already shown in
            // the badge doubling as an importance signal.
            const zIndex = 200 + visibleCount;
            if (existingMarker) {
                // Update existing marker content, position, and z-index (count -
                // and therefore priority - can change between calls)
                existingMarker.content = content;
                existingMarker.position = { lat: centerLat, lng: centerLng };
                existingMarker.zIndex = zIndex;
                existingMarker.map = this.state.map;
            }
            else {
                // Create new marker with high z-index to appear above polygons and listing markers
                const marker = new AdvancedMarkerElement({
                    position: { lat: centerLat, lng: centerLng },
                    map: this.state.map,
                    content,
                    title: formatNeighborhoodName(neighborhood.name),
                    zIndex,
                });
                // Store neighborhood reference for click handling
                marker.neighborhoodData = neighborhood;
                this.state.neighborhoodMarkers.set(neighborhood.id, marker);
            }
        }
    }
    /**
     * Hide all neighborhood markers
     */
    hideNeighborhoodMarkers() {
        for (const marker of this.state.neighborhoodMarkers.values()) {
            marker.map = null;
        }
    }
    /**
     * Create neighborhood marker content based on zoom
     * v1-style: uses separate badge for count, dims label when no matching properties
     */
    createNeighborhoodMarkerContent(neighborhood, showLabel, visibleCount, listings, iconOnly = false) {
        const content = document.createElement('div');
        content.style.cssText = `
      display: inline-block;
      cursor: pointer;
      position: relative;
    `;
        if (iconOnly) {
            // Minimal marker for a neighborhood directly favorited/alerted as a
            // whole (not via any individual listing inside it) - shown below the
            // normal zoom crossover since there's nothing else on the map
            // representing it yet. Just the attribute badge(s) in a small pill,
            // no name/count/fill chrome.
            const badgeFlags = this.computeNeighborhoodBadgeFlags(listings, neighborhood.id);
            const activeBadges = this.activeBadgeEntries((entry) => !!badgeFlags[entry.id]);
            if (activeBadges.length === 0)
                return content;
            const isDesktop = window.innerWidth >= 1024;
            const scale = isDesktop ? 1.5 : 1;
            const badgeSize = Math.round((BADGE_SIZE_BASE + 2) * scale);
            const pill = document.createElement('div');
            pill.style.cssText = `
        display: flex;
        gap: 1px;
        background: white;
        border-radius: ${badgeSize / 2 + 3}px;
        padding: 2px;
        box-shadow: 0 1px 3px rgba(0,0,0,0.4);
      `;
            pill.innerHTML = activeBadges
                .map(b => `<div style="width:${badgeSize}px;height:${badgeSize}px;display:flex;align-items:center;justify-content:center;color:${b.color};">${b.svg}</div>`)
                .join('');
            content.appendChild(pill);
            return content;
        }
        // Marker container
        const marker = document.createElement('div');
        const count = visibleCount || 0;
        // Neighborhood names used to fade to 30% opacity (and the compact
        // marker shrink a couple px) whenever the currently-active status had
        // zero matches here - visibleCount only ever counts Active listings, so
        // a neighborhood with real Pending/Sold inventory but no current Active
        // listing looked "dead" purely because of which status happened to be
        // toggled on. Always render at full weight now; the count badge below
        // is still the actual for-sale signal.
        const bgColor = '#2c5530'; // Always dark green base
        if (showLabel) {
            // Labeled marker (zoom >= 15) - v1 style with positioned badge in upper
            // right. Font/padding/ring scale down toward NEIGHBORHOOD_LABEL_MIN_SCALE
            // right as labels first appear (dense areas like Wailea/Kihei are most
            // crowded right at that threshold), growing back to full size once
            // zoomed in further - see rescaleNeighborhoodLabels(), which re-applies
            // this on every zoom_changed without a full content rebuild.
            const scale = this.currentNeighborhoodLabelScale();
            marker.className = 'neighborhood-name';
            marker.dataset.neighborhoodLabeled = '1';
            marker.style.cssText = `
        background: ${bgColor};
        color: white;
        padding: ${(6 * scale).toFixed(1)}px ${(10 * scale).toFixed(1)}px;
        border-radius: 6px;
        font-size: ${(NEIGHBORHOOD_LABEL_FONT_SIZE_BASE * scale).toFixed(1)}px;
        font-weight: 500;
        white-space: nowrap;
        box-shadow: 0 0 0 ${(2 * scale).toFixed(1)}px white, 0 2px 4px rgba(0, 0, 0, 0.3);
        position: relative;
      `;
            marker.textContent = formatNeighborhoodName(neighborhood.name);
            // v1-style badge in upper right corner (positioned, not inline)
            if (count > 0) {
                const badge = document.createElement('div');
                badge.style.cssText = `
          position: absolute;
          top: -7px;
          right: -7px;
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 14px;
          height: 14px;
          padding: 0 3px;
          background: #22c55e;
          color: white;
          font-size: 9px;
          font-weight: 700;
          border-radius: 7px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
          border: 1.5px solid white;
          pointer-events: none;
        `;
                badge.textContent = count.toString();
                marker.appendChild(badge);
            }
        }
        else {
            // Compact/unlabeled marker (13.5 <= zoom < 15) - small rounded rectangle
            // v1-style: 20x12px rectangle with badge in upper right
            marker.className = 'neighborhood-name';
            marker.style.cssText = `
        width: 20px;
        height: 12px;
        background: ${bgColor};
        border-radius: 4px;
        box-shadow: 0 0 0 2px white, 0 2px 4px rgba(0, 0, 0, 0.3);
        position: relative;
      `;
            // Badge on upper right showing count (only when not de-emphasized)
            if (count > 0) {
                const badge = document.createElement('div');
                badge.style.cssText = `
          position: absolute;
          top: -6px;
          right: -6px;
          min-width: 16px;
          height: 16px;
          padding: 0 4px;
          background: #22c55e;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          font-weight: 700;
          color: white;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
          border: 1.5px solid white;
          pointer-events: none;
        `;
                badge.textContent = count.toString();
                marker.appendChild(badge);
            }
        }
        // Add badges based on listings in neighborhood (open house, new, favorites)
        const badges = this.renderNeighborhoodBadges(listings, neighborhood.id);
        if (badges) {
            marker.insertAdjacentHTML('beforeend', badges);
        }
        content.appendChild(marker);
        return content;
    }
    /**
     * Which attribute badges apply to a neighborhood (New/Open House/
     * Favorites/Alerts), given its listings and the current badgeOptions.
     * Shared by renderNeighborhoodBadges (what to draw) and
     * showNeighborhoodMarkers's rebuild signature (whether to redraw) - a
     * single source of truth so the two can't silently disagree.
     */
    computeNeighborhoodBadgeFlags(listings, neighborhoodId) {
        // Check if any listing in neighborhood matches each active attribute,
        // OR - for id-set attributes only - if the neighborhood itself is
        // directly favorited/subscribed to (a distinct key from any individual
        // listing's - see alerts.ts/favorites.ts's `nh:${id}` prefix
        // convention). Without that check, subscribing to a whole neighborhood
        // directly never showed a badge unless a property within it also
        // happened to be individually favorited/alerted - confirmed live
        // 2026-09. Gated on badgeOptions.show per attribute (not just left to
        // activeBadgeEntries downstream) so the signature this feeds stays
        // stable while that attribute is toggled off, same as before.
        const factsList = listings.map(factsFromListingSummary);
        const flags = {};
        for (const entry of ATTRIBUTE_REGISTRY) {
            if (!this.badgeOptions.show[entry.id]) {
                flags[entry.id] = false;
                continue;
            }
            const matchesAnyListing = factsList.some((f) => entry.matches(f, this.attributeIdSets));
            const matchesNeighborhoodItself = entry.kind === 'id-set' && !!neighborhoodId &&
                (this.attributeIdSets[entry.id]?.has(`nh:${neighborhoodId}`) ?? false);
            flags[entry.id] = matchesAnyListing || matchesNeighborhoodItself;
        }
        return flags;
    }
    /**
     * Render badges for a neighborhood as horizontal row below marker
     */
    renderNeighborhoodBadges(listings, neighborhoodId) {
        // Only bail early when there's truly nothing to check - a neighborhood
        // with zero current listings can still match via a direct nh:-prefixed
        // favorite/alert (computeNeighborhoodBadgeFlags handles an empty
        // listings array fine), so don't skip that check just because it has
        // no listings right now.
        if ((!listings || listings.length === 0) && !neighborhoodId)
            return '';
        // Same base size + desktop boost as property/cluster badges (see
        // BADGE_SIZE_BASE) - was a flat 12px, smaller than even the mobile
        // property badge size, with nothing scaling it up for desktop.
        const isDesktop = window.innerWidth >= 1024;
        const scale = isDesktop ? 1.5 : 1;
        const badgeSize = Math.round(BADGE_SIZE_BASE * scale);
        const badgeFlags = this.computeNeighborhoodBadgeFlags(listings, neighborhoodId);
        const activeBadges = this.activeBadgeEntries((entry) => !!badgeFlags[entry.id]);
        if (activeBadges.length === 0) {
            return '';
        }
        // Create horizontal badge row overlapping bottom of neighborhood marker
        const badgeHtml = activeBadges
            .map(b => `<div style="width:${badgeSize}px;height:${badgeSize}px;display:flex;align-items:center;justify-content:center;color:${b.color};filter:drop-shadow(0 1px 1px rgba(0,0,0,0.3));">${b.svg}</div>`)
            .join('');
        // Anchor mostly below the label rather than overlapping it - only
        // ~30% of the badge height overlaps upward into the label (enough to
        // still read as "attached to the bottom edge"), the rest hangs into
        // open space below. A fixed -4px offset worked fine at the old flat
        // 12px badge size, but left most of a 23px (desktop) badge sitting on
        // top of the label, obscuring the neighborhood name.
        const badgeOverlap = Math.round(badgeSize * 0.3);
        const badgeBottomOffset = -(badgeSize - badgeOverlap);
        return `<div style="position:absolute;bottom:${badgeBottomOffset}px;left:50%;transform:translateX(-50%);display:flex;gap:1px;pointer-events:none;">${badgeHtml}</div>`;
    }
    /**
     * Get listings grouped by neighborhood
     */
    getListingsByNeighborhood() {
        const grouped = new Map();
        for (const listing of this.state.listings) {
            if (listing.neighborhoodId) {
                if (!grouped.has(listing.neighborhoodId)) {
                    grouped.set(listing.neighborhoodId, []);
                }
                grouped.get(listing.neighborhoodId).push(listing);
            }
        }
        return grouped;
    }
    escapeHtml(str) {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
    }
    /**
     * Hole rings to punch into an "Other" catch-all's polygon - every other
     * loaded neighborhood (excluding itself and other catch-alls) whose
     * bounding box overlaps this one's, winding-corrected so Google Maps
     * actually treats each as a cutout. this.state.neighborhoods is already
     * fully populated by the time addNeighborhood runs (setNeighborhoods sets
     * it before looping), so every real sibling is available here regardless
     * of insertion order.
     */
    getHoleRingsFor(outerRing, ownId) {
        const outerBounds = ringBounds(outerRing);
        const holes = [];
        for (const other of this.state.neighborhoods) {
            if (other.id === ownId)
                continue;
            if (!other.polygon || other.polygon.length < 3)
                continue;
            if (isOtherCatchAllNeighborhood(other.name))
                continue;
            const ring = other.polygon.map((p) => ({ lat: p.lat, lng: p.lng }));
            if (!boundsOverlap(outerBounds, ringBounds(ring)))
                continue;
            holes.push(asOppositeWinding(outerRing, ring));
        }
        return holes;
    }
    addNeighborhood(neighborhood) {
        if (!this.state.map || !neighborhood.polygon?.length)
            return;
        const outerRing = neighborhood.polygon.map((p) => ({ lat: p.lat, lng: p.lng }));
        const isOther = isOtherCatchAllNeighborhood(neighborhood.name);
        // "Other" catch-alls get every overlapping neighborhood's ring punched
        // into them as a hole - see the comment above isOtherCatchAllNeighborhood
        // for why. Always pass paths as an array-of-rings (not a bare ring) so
        // the non-"Other" case doesn't need a separate PolygonOptions shape.
        const paths = isOther ? [outerRing, ...this.getHoleRingsFor(outerRing, neighborhood.id)] : [outerRing];
        const style = neighborhood.strAllowed ? POLYGON_STYLES.strAllowed : POLYGON_STYLES.default;
        const polygon = new google.maps.Polygon({
            paths,
            ...style,
            map: this.state.map,
            visible: false, // Hidden by default, only shown in neighborhood mode
            // "Other" renders behind real neighborhoods - on top of the hole
            // cutouts above, this keeps its stroke/label from visually competing
            // with a more specific neighborhood sitting on the same ground.
            zIndex: isOther ? 1 : 10,
        });
        // Store reference
        this.state.polygons.set(neighborhood.id, polygon);
        // Add event listeners
        polygon.addListener('mouseover', () => {
            if (this.state.selectedNeighborhoodId !== neighborhood.id) {
                polygon.setOptions(POLYGON_STYLES.hover);
            }
        });
        polygon.addListener('mouseout', () => {
            if (this.state.selectedNeighborhoodId !== neighborhood.id) {
                polygon.setOptions(this.getNeighborhoodBaseStyle(neighborhood));
            }
        });
        polygon.addListener('click', () => {
            this.selectNeighborhood(neighborhood.id);
            if (this.options.onNeighborhoodClick) {
                this.options.onNeighborhoodClick(neighborhood);
            }
        });
        // Add label
        this.addNeighborhoodLabel(neighborhood);
        // Add badge if for sale
        if (neighborhood.forSaleCount && neighborhood.forSaleCount > 0) {
            this.addNeighborhoodBadge(neighborhood);
        }
    }
    /**
     * The style a neighborhood polygon should have when nothing is actively
     * overriding it (i.e. not hovered, not selected) - depends on whichever
     * fill mode is currently active. Used on mouseout so hovering away
     * restores the *actual current* fill instead of unconditionally reverting
     * to the plain unfilled style - that mismatch is exactly what made the
     * Market Trends color disappear on hover before this existed (mouseout
     * only ever knew how to restore Zoning or the default, never a price
     * color, since it predates Market Trends).
     */
    getNeighborhoodBaseStyle(neighborhood) {
        if (this.state.neighborhoodFillMode === 'zoning') {
            const zoning = normalizeZoningCategory(neighborhood.zoning);
            const colors = ZONING_COLORS[zoning];
            return { fillColor: colors.fill, fillOpacity: ZONING_FILL_OPACITY, strokeColor: colors.stroke };
        }
        if (this.state.neighborhoodFillMode === 'marketTrends') {
            const entry = this.state.neighborhoodPriceData.get(neighborhood.id);
            const { min, center, max } = this.state.neighborhoodPriceRange;
            const color = entry && entry.sampleSize >= MARKET_TRENDS_MIN_SAMPLE_SIZE
                ? getMarketTrendsColor(entry.median, min, center, max)
                : MARKET_TRENDS_NO_DATA_COLOR;
            return { fillColor: color, fillOpacity: MARKET_TRENDS_FILL_OPACITY, strokeColor: color };
        }
        if (this.state.neighborhoodFillMode === 'climate') {
            const entry = this.state.neighborhoodClimateData.get(neighborhood.id);
            const { min, center, max } = this.state.neighborhoodClimateRange;
            const ramp = CLIMATE_RAMPS[this.state.activeClimateDatatype];
            const color = entry
                ? getClimateColor(ramp, entry.value, min, center, max)
                : CLIMATE_NO_DATA_COLOR;
            return { fillColor: color, fillOpacity: CLIMATE_FILL_OPACITY, strokeColor: color };
        }
        return neighborhood.strAllowed ? POLYGON_STYLES.strAllowed : POLYGON_STYLES.default;
    }
    addNeighborhoodLabel(neighborhood) {
        if (!this.state.map)
            return;
        const label = new google.maps.Marker({
            position: { lat: neighborhood.centerLat, lng: neighborhood.centerLng },
            map: this.state.map,
            label: {
                text: formatNeighborhoodName(neighborhood.name),
                color: '#1f2937',
                fontSize: '12px',
                fontWeight: '500',
            },
            icon: {
                path: google.maps.SymbolPath.CIRCLE,
                scale: 0,
            },
        });
        this.state.labels.set(neighborhood.id, label);
    }
    addNeighborhoodBadge(neighborhood) {
        if (!this.state.map || !neighborhood.forSaleCount)
            return;
        // Offset badge slightly from center
        const badge = new google.maps.Marker({
            position: {
                lat: neighborhood.centerLat + 0.001,
                lng: neighborhood.centerLng,
            },
            map: this.state.map,
            label: {
                text: neighborhood.forSaleCount.toString(),
                color: '#ffffff',
                fontSize: '10px',
                fontWeight: 'bold',
            },
            icon: {
                path: google.maps.SymbolPath.CIRCLE,
                scale: 12,
                fillColor: CLUSTER_COLOR,
                fillOpacity: 1,
                strokeColor: '#ffffff',
                strokeWeight: 2,
            },
        });
        this.state.badges.set(neighborhood.id, badge);
    }
    selectNeighborhood(id) {
        // Deselect previous
        if (this.state.selectedNeighborhoodId) {
            const prevPolygon = this.state.polygons.get(this.state.selectedNeighborhoodId);
            if (prevPolygon) {
                prevPolygon.setOptions(POLYGON_STYLES.default);
            }
        }
        // Select new
        this.state.selectedNeighborhoodId = id;
        if (id) {
            const polygon = this.state.polygons.get(id);
            if (polygon) {
                polygon.setOptions(POLYGON_STYLES.selected);
            }
        }
    }
    // Full teardown - used by clearAll() (map destroy). Also wipes the
    // AdvancedMarkerElement neighborhood-count bubbles and their signature
    // cache, which is correct here (nothing to preserve) but was wrong for
    // setNeighborhoods() to reuse - see clearNeighborhoodPolygonsAndLegacyPins.
    clearNeighborhoods() {
        this.clearNeighborhoodPolygonsAndLegacyPins();
        for (const marker of this.state.neighborhoodMarkers.values()) {
            marker.map = null;
        }
        this.state.neighborhoodMarkers.clear();
        this.lastNeighborhoodMarkerSignature.clear();
    }
    /**
     * Clears polygons and the legacy label/badge google.maps.Marker pins
     * (addNeighborhoodLabel/addNeighborhoodBadge) - but deliberately leaves
     * the AdvancedMarkerElement neighborhood-count bubbles
     * (state.neighborhoodMarkers) and their signature cache alone.
     *
     * setNeighborhoods() calls this instead of the full clearNeighborhoods()
     * specifically so those bubbles survive across repeated calls -
     * updateNeighborhoodMarkers() already has its own per-marker signature
     * check to skip rebuilding a bubble whose content hasn't changed, but
     * that check was previously worthless: setNeighborhoods() is called
     * twice on every page load by design (an instant lightweight pass with
     * summary data, then a background upgrade once the full polygon geometry
     * loads - see loadFullNeighborhoodPolygons in apps/public/src/main.ts),
     * and wiping the signature cache between those two calls meant every
     * bubble looked "new" the second time and got torn down and rebuilt
     * even when nothing about it had actually changed - a visible flash on
     * every single page load.
     */
    clearNeighborhoodPolygonsAndLegacyPins() {
        for (const polygon of this.state.polygons.values()) {
            polygon.setMap(null);
        }
        for (const label of this.state.labels.values()) {
            label.setMap(null);
        }
        for (const badge of this.state.badges.values()) {
            badge.setMap(null);
        }
        this.state.polygons.clear();
        this.state.labels.clear();
        this.state.badges.clear();
        this.state.neighborhoods = [];
        this.state.selectedNeighborhoodId = null;
        // labels/badges just got torn down and will be recreated from scratch
        // by addNeighborhoodLabel/addNeighborhoodBadge (each a plain,
        // default-visible google.maps.Marker with no explicit initial
        // setVisible call) - updateVisibility()'s showLabels/showBadges skip-
        // gate compares only those booleans, so without this reset a rebuild
        // where the booleans haven't changed (the common case: setNeighborhoods
        // runs a lightweight pass then a full-polygon upgrade pass back to
        // back, same zoom both times) would skip the very call that's supposed
        // to hide the brand-new markers, leaving unstyled black-text labels
        // visible on screen.
        this.lastVisibilityState = null;
        // Exit neighborhood mode if active
        if (this.state.neighborhoodMode.active) {
            this.exitNeighborhoodMode();
        }
    }
    // ==================== GEOGRAPHY (REGIONS/DISTRICTS) ====================
    //
    // Region/District label markers, always on the map once loaded - no
    // zoom-band show/hide like neighborhood markers have. Google Maps'
    // AdvancedMarkerElement already viewport-culls natively (confirmed while
    // fixing neighborhood-marker rebuild performance earlier), so there's no
    // custom visibility logic needed here at all: create once, leave in
    // place. Todd hand-places label_lat/label_lng in open water/mountain
    // space specifically so labels never compete visually with neighborhood
    // markers or listing pins regardless of zoom. Size DOES still respond to
    // zoom though - see rescaleGeographyLabels(), hooked into zoom_changed -
    // full-size labels at island-wide zoom crowded the map.
    setCounties(counties) {
        if (!this.state.map)
            return;
        this.clearCounties();
        this.state.counties = counties;
        void this.createGeographyMarkers(counties, this.state.countyMarkers, 'county');
    }
    setRegions(regions) {
        if (!this.state.map)
            return;
        this.clearRegions();
        this.state.regions = regions;
        void this.createGeographyMarkers(regions, this.state.regionMarkers, 'region');
    }
    setDistricts(districts) {
        if (!this.state.map)
            return;
        this.clearDistricts();
        this.state.districts = districts;
        this.districtHoleRingsCache = null;
        void this.createGeographyMarkers(districts, this.state.districtMarkers, 'district');
        for (const district of districts) {
            this.addDistrictPolygon(district);
        }
    }
    clearCounties() {
        for (const marker of this.state.countyMarkers.values()) {
            marker.map = null;
        }
        this.state.countyMarkers.clear();
        this.state.counties = [];
    }
    clearRegions() {
        for (const marker of this.state.regionMarkers.values()) {
            marker.map = null;
        }
        this.state.regionMarkers.clear();
        this.state.regions = [];
    }
    clearDistricts() {
        for (const marker of this.state.districtMarkers.values()) {
            marker.map = null;
        }
        this.state.districtMarkers.clear();
        this.state.districts = [];
        for (const polygon of this.state.districtPolygons.values()) {
            polygon.setMap(null);
        }
        this.state.districtPolygons.clear();
        for (const outline of this.state.districtOutlines.values()) {
            outline.setMap(null);
        }
        this.state.districtOutlines.clear();
    }
    // District layer is two objects per district, kept in sync:
    // - districtPolygons: invisible stroke, used only for the Market Trends
    //   fill (see setDistrictFillMode) - a plain google.maps.Polygon stroke
    //   can't be dashed (confirmed elsewhere in this file, see the NHM-mode
    //   entry polygon a few hundred lines down), so it isn't used for the
    //   visible border at all.
    // - districtOutlines: the actual visible boundary - a closed Polyline
    //   (light gray, dashed) so it reads as a subtle reference line, not
    //   competing with neighborhood polygons or listing pins.
    // Not clickable - these exist purely as a visual boundary, unlike
    // neighborhood polygons which have their own click-to-enter-NHM behavior.
    addDistrictPolygon(district) {
        if (!this.state.map || !district.polygon?.length)
            return;
        const path = district.polygon.map((p) => ({ lat: p.lat, lng: p.lng }));
        const fillActive = this.state.districtFillMode === 'marketTrends' || this.state.districtFillMode === 'climate';
        const polygon = new google.maps.Polygon({
            paths: path,
            strokeOpacity: 0,
            fillColor: resolveGeographyLabelColor(district, 'district'),
            fillOpacity: 0,
            map: this.state.map,
            visible: this.state.districtOutlineEnabled || fillActive,
            clickable: false,
            zIndex: 0, // below neighborhood polygons ("Other" catch-all=1, real=10), above the base map
        });
        this.state.districtPolygons.set(district.id, polygon);
        const outline = new google.maps.Polyline({
            path: [...path, path[0]], // close the ring
            strokeOpacity: 0, // hide the solid line, dashes drawn via icons below
            icons: [{
                    icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, strokeColor: DISTRICT_OUTLINE_COLOR, scale: 2 },
                    offset: '0',
                    repeat: '12px',
                }],
            map: this.state.map,
            visible: this.state.districtOutlineEnabled,
            clickable: false,
            zIndex: 31,
        });
        this.state.districtOutlines.set(district.id, outline);
    }
    /**
     * Display tab "District" > Outline on/off - shows/hides the dashed
     * boundary line. Independent of Labels and of Trends fill (a filled
     * district polygon stays visible via applyDistrictFill's own
     * visibility handling regardless of this toggle).
     */
    setDistrictOutlineEnabled(enabled) {
        this.state.districtOutlineEnabled = enabled;
        for (const outline of this.state.districtOutlines.values()) {
            outline.setVisible(enabled);
        }
        // Fill polygon visibility depends on both Outline and Trends - let the
        // one function that already knows that relationship decide.
        this.applyDistrictFill();
    }
    isDistrictOutlineEnabled() {
        return this.state.districtOutlineEnabled;
    }
    /** Display tab "District" > Labels on/off - shows/hides the district name markers. */
    setDistrictsEnabled(enabled) {
        this.state.districtsEnabled = enabled;
        for (const marker of this.state.districtMarkers.values()) {
            marker.map = enabled ? this.state.map : null;
        }
    }
    isDistrictsEnabled() {
        return this.state.districtsEnabled;
    }
    async createGeographyMarkers(entities, markerMap, level) {
        if (!this.state.map)
            return;
        const { AdvancedMarkerElement } = await this.getMarkerLibrary();
        for (const entity of entities) {
            // Prefer the hand-placed label position; fall back to the polygon
            // centroid (already computed server-side) so an entity still shows
            // *something* before its label position has been set - see plan note
            // on this being a known transitional state, not a bug.
            const lat = entity.labelLat ?? entity.centerLat;
            const lng = entity.labelLng ?? entity.centerLng;
            if (lat == null || lng == null)
                continue;
            const content = this.createGeographyMarkerContent(entity, level);
            content.addEventListener('click', () => {
                if (level === 'county') {
                    this.options.onCountyClick?.(entity);
                }
                else if (level === 'region') {
                    this.options.onRegionClick?.(entity);
                }
                else {
                    this.options.onDistrictClick?.(entity);
                }
            });
            // County and Region have no Labels toggle (always on); districts do
            // (Display tab "District" > Labels, default off - see districtsEnabled).
            const initiallyVisible = level === 'district' ? this.state.districtsEnabled : true;
            const marker = new AdvancedMarkerElement({
                position: { lat, lng },
                map: initiallyVisible ? this.state.map : null,
                content,
                title: entity.name,
                // County below Region below District below Neighborhood (zIndex
                // 200), so the most-local label wins if two ever end up close
                // together.
                zIndex: level === 'county' ? 100 : level === 'region' ? 120 : 150,
            });
            markerMap.set(entity.id, marker);
        }
    }
    createGeographyMarkerContent(entity, level) {
        const content = document.createElement('div');
        // Stash the resolved color on the element itself - rescaleGeographyLabels()
        // re-applies it on every zoom change without needing the original entity
        // (region_id) around, since a district's district-level info can't be
        // rederived from the AdvancedMarkerElement alone.
        content.dataset.labelColor = resolveGeographyLabelColor(entity, level);
        this.applyGeographyLabelStyle(content, level, content.dataset.labelColor, this.currentGeographyLabelScale());
        content.textContent = formatNeighborhoodName(entity.name);
        return content;
    }
    currentGeographyLabelScale() {
        const zoom = this.state.map?.getZoom() ?? DEFAULT_ZOOM;
        return computeGeographyLabelScale(zoom);
    }
    currentNeighborhoodLabelScale() {
        const zoom = this.state.map?.getZoom() ?? DEFAULT_ZOOM;
        return computeNeighborhoodLabelScale(zoom);
    }
    applyGeographyLabelStyle(content, level, color, scale) {
        const base = GEOGRAPHY_LABEL_BASE[level];
        content.style.cssText = `
      display: inline-block;
      cursor: pointer;
      background: ${hexToRgba(color, base.alpha)};
      color: white;
      padding: ${(base.padV * scale).toFixed(1)}px ${(base.padH * scale).toFixed(1)}px;
      border-radius: 6px;
      font-size: ${(base.fontSize * scale).toFixed(1)}px;
      font-weight: ${base.fontWeight};
      letter-spacing: ${base.letterSpacing};
      white-space: nowrap;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    `;
    }
    /**
     * Re-applies region/district label sizing for the current zoom - called
     * on zoom_changed so labels shrink when zoomed out instead of staying a
     * fixed size that crowds the map at island-wide views.
     */
    rescaleGeographyLabels() {
        if (this.state.countyMarkers.size === 0 && this.state.regionMarkers.size === 0 && this.state.districtMarkers.size === 0)
            return;
        const scale = this.currentGeographyLabelScale();
        for (const marker of this.state.countyMarkers.values()) {
            const content = marker.content;
            if (content instanceof HTMLElement) {
                this.applyGeographyLabelStyle(content, 'county', content.dataset.labelColor ?? COUNTY_LABEL_COLOR, scale);
            }
        }
        for (const marker of this.state.regionMarkers.values()) {
            const content = marker.content;
            if (content instanceof HTMLElement) {
                this.applyGeographyLabelStyle(content, 'region', content.dataset.labelColor ?? GEOGRAPHY_LABEL_FALLBACK_COLOR, scale);
            }
        }
        for (const marker of this.state.districtMarkers.values()) {
            const content = marker.content;
            if (content instanceof HTMLElement) {
                this.applyGeographyLabelStyle(content, 'district', content.dataset.labelColor ?? GEOGRAPHY_LABEL_FALLBACK_COLOR, scale);
            }
        }
    }
    /**
     * Re-applies neighborhood label sizing for the current zoom - same idea
     * as rescaleGeographyLabels() above, just targeting whichever neighborhood
     * markers are currently in "labeled" mode (marked via
     * dataset.neighborhoodLabeled in createNeighborhoodMarkerContent; compact
     * unlabeled markers below NEIGHBORHOOD_LABELS_ZOOM have no font to scale
     * and are skipped). Mutates the existing DOM node directly rather than
     * rebuilding marker content, so it's cheap enough to run on every
     * zoom_changed tick.
     */
    rescaleNeighborhoodLabels() {
        if (this.state.neighborhoodMarkers.size === 0)
            return;
        const scale = this.currentNeighborhoodLabelScale();
        for (const marker of this.state.neighborhoodMarkers.values()) {
            const content = marker.content;
            if (!(content instanceof HTMLElement))
                continue;
            const inner = content.firstElementChild;
            if (!(inner instanceof HTMLElement) || inner.dataset.neighborhoodLabeled !== '1')
                continue;
            inner.style.padding = `${(6 * scale).toFixed(1)}px ${(10 * scale).toFixed(1)}px`;
            inner.style.fontSize = `${(NEIGHBORHOOD_LABEL_FONT_SIZE_BASE * scale).toFixed(1)}px`;
            inner.style.boxShadow = `0 0 0 ${(2 * scale).toFixed(1)}px white, 0 2px 4px rgba(0, 0, 0, 0.3)`;
        }
    }
    // ==================== NEIGHBORHOOD MODE ====================
    /**
     * Enter neighborhood mode for a specific neighborhood
     */
    async enterNeighborhoodMode(neighborhoodId) {
        if (!this.state.map)
            return;
        // Guard against race conditions from rapid taps on mobile
        if (this.enteringNeighborhoodMode) {
            console.log(`[NHM] Already entering NHM, ignoring duplicate request`);
            return;
        }
        const neighborhood = this.state.neighborhoods.find(n => n.id === neighborhoodId);
        if (!neighborhood || !neighborhood.polygon?.length) {
            console.warn(`[NHM] Neighborhood ${neighborhoodId} not found or has no polygon`);
            return;
        }
        console.log(`[NHM enterNeighborhoodMode] Entering NHM for ${neighborhood.name}`);
        console.log(`[NHM enterNeighborhoodMode] Current activeStatuses:`, Array.from(this.state.activeStatuses));
        // Set guard flag
        this.enteringNeighborhoodMode = true;
        // Exit any existing mode
        if (this.state.neighborhoodMode.active) {
            this.exitNeighborhoodMode();
        }
        // Set mode state
        this.state.neighborhoodMode.active = true;
        this.state.neighborhoodMode.neighborhoodId = neighborhoodId;
        this.state.neighborhoodMode.neighborhood = neighborhood;
        // Create dashed polygon border in medium-blue
        const path = neighborhood.polygon.map(p => ({ lat: p.lat, lng: p.lng }));
        console.log(`[NHM] Creating polygon with ${path.length} points`);
        // Validate path has enough points, all with finite coordinates -
        // feeding even one NaN/invalid point into the fitBounds() call below
        // can snap the camera out to Google's whole-Earth view. Same guard
        // loadAndShowBuildings already applies to building footprints.
        const hasInvalidPoint = path.some((p) => !Number.isFinite(p.lat) || !Number.isFinite(p.lng));
        if (path.length < 3 || hasInvalidPoint) {
            console.error(`[NHM] Invalid polygon - ${path.length} points, hasInvalidPoint=${hasInvalidPoint}`);
            this.enteringNeighborhoodMode = false;
            return;
        }
        // Close the polygon path for the polyline
        const closedPath = [...path, path[0]];
        // Use Polyline with dashed pattern (Polygon doesn't support dashes)
        try {
            const modePolygon = new google.maps.Polyline({
                path: closedPath,
                strokeColor: '#3b82f6', // Medium blue
                strokeWeight: 2,
                strokeOpacity: 0, // Hide solid stroke, use icons for dashes
                icons: [{
                        icon: {
                            path: 'M 0,-1 0,1',
                            strokeOpacity: 1,
                            strokeColor: '#3b82f6',
                            scale: 3,
                        },
                        offset: '0',
                        repeat: '15px',
                    }],
                map: this.state.map,
            }); // Cast for type compatibility
            this.state.neighborhoodMode.polygon = modePolygon;
            console.log(`[NHM] Polygon created successfully`);
        }
        catch (error) {
            console.error(`[NHM] Failed to create polygon:`, error);
            this.enteringNeighborhoodMode = false;
            return;
        }
        // Hide the existing polygon for this neighborhood
        const existingPolygon = this.state.polygons.get(neighborhoodId);
        if (existingPolygon) {
            existingPolygon.setVisible(false);
        }
        // Hide the neighborhood marker for this neighborhood
        const existingMarker = this.state.neighborhoodMarkers.get(neighborhoodId);
        if (existingMarker) {
            existingMarker.map = null;
        }
        // Calculate and store polygon bounds for floating label positioning
        const bounds = new google.maps.LatLngBounds();
        for (const point of path) {
            bounds.extend(point);
        }
        this.state.neighborhoodMode.polygonBounds = bounds;
        // Create floating label (positioned above polygon top)
        this.createFloatingLabel(neighborhood);
        // Extend bounds northward to include space for the floating label
        // The label is ~40px tall, we need to add equivalent latitude space above the polygon
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        const latSpan = ne.lat() - sw.lat();
        // Add 20% more space above for the label
        const labelSpace = latSpan * 0.2;
        const extendedBounds = new google.maps.LatLngBounds(sw, new google.maps.LatLng(ne.lat() + labelSpace, ne.lng()));
        // Zoom to fit extended bounds (includes label space) within the portion
        // of the map not covered by the UnifiedDrawer, if it's open.
        this.state.map.fitBounds(extendedBounds, { top: 60, right: 50 + this.rightInsetPx, bottom: 50, left: 50 });
        // Wait for map to settle using 'idle' event (more reliable than fixed timeout on mobile)
        const onMapIdle = () => {
            if (!this.state.map)
                return;
            // Remove this one-time listener
            google.maps.event.clearListeners(this.state.map, 'idle');
            // Re-add the normal bounds change listener
            this.state.map.addListener('idle', () => {
                this.handleBoundsChanged();
            });
            // Ensure minimum zoom
            const currentZoom = this.state.map.getZoom() ?? DEFAULT_ZOOM;
            if (currentZoom < NEIGHBORHOOD_MODE_ZOOM) {
                this.state.map.setZoom(NEIGHBORHOOD_MODE_ZOOM);
                // Need to wait for another idle after setZoom
                const onZoomIdle = () => {
                    google.maps.event.clearListeners(this.state.map, 'idle');
                    this.state.map.addListener('idle', () => {
                        this.handleBoundsChanged();
                    });
                    this.finalizeNeighborhoodModeEntry();
                };
                this.state.map.addListener('idle', onZoomIdle);
            }
            else {
                this.finalizeNeighborhoodModeEntry();
            }
        };
        // Temporarily replace idle listener to catch the fitBounds completion
        google.maps.event.clearListeners(this.state.map, 'idle');
        this.state.map.addListener('idle', onMapIdle);
        // Load buildings and NHM data in PARALLEL for faster NHM entry
        const perfStart = performance.now();
        console.log(`[NHM PERF] Starting parallel load...`);
        // Residential (single-family) neighborhoods have no "buildings" to
        // extrude - the concept only applies to condo/hotel complexes - so skip
        // the fetch entirely rather than round-tripping for an empty result.
        // Also checked directly by zoning (not just neighborhoodType): AG-zoned
        // neighborhoods are farm/ranch lots regardless of what neighborhoodType
        // says, and neighborhoodType has been confirmed wrong for AG before
        // (defaulted to 'condo' - see isAgriculturalZoning's doc comment).
        const skipBuildings = neighborhood.neighborhoodType === 'residential' || isAgriculturalZoning(neighborhood.zoning);
        const buildingsPromise = skipBuildings
            ? Promise.resolve()
            : this.loadAndShowBuildings(neighborhoodId).then(result => {
                console.log(`[NHM PERF] Buildings loaded in ${(performance.now() - perfStart).toFixed(0)}ms`);
                return result;
            });
        // IMPORTANT: Load ALL statuses once, then filter client-side for instant toggling
        // This avoids API calls when user toggles status checkboxes
        const allStatuses = 'ForSale,Pending,RecentlySold,OffMarket';
        console.log(`[NHM enterNeighborhoodMode] Loading ALL statuses: ${allStatuses}`);
        console.log(`[NHM enterNeighborhoodMode] Current activeStatuses for display: ${Array.from(this.state.activeStatuses)}`);
        // Prefer onLoadMarkers (new tmk_status endpoint) over legacy callbacks
        let dataPromise;
        if (this.options.onLoadMarkers) {
            console.log(`[NHM enterNeighborhoodMode] Using onLoadMarkers (tmk_status endpoint)`);
            const markersStart = performance.now();
            dataPromise = this.options.onLoadMarkers({ neighborhoodId, status: allStatuses })
                .then(markers => {
                console.log(`[NHM PERF] Markers API call took ${(performance.now() - markersStart).toFixed(0)}ms`);
                console.log(`[NHM enterNeighborhoodMode] ========== MARKERS LOADED ==========`);
                console.log(`[NHM enterNeighborhoodMode] Total markers received: ${markers.length}`);
                this.state.neighborhoodMode.tmkStatusMarkers = markers;
                // Detailed status breakdown
                const statusCounts = { ForSale: 0, Pending: 0, RecentlySold: 0, OffMarket: 0 };
                for (const m of markers) {
                    statusCounts[m.status] = (statusCounts[m.status] || 0) + 1;
                }
                console.log(`[NHM enterNeighborhoodMode] Status breakdown:`, statusCounts);
                // Warn if no OffMarket markers (likely migration not run)
                if (statusCounts['OffMarket'] === 0) {
                    console.warn(`[NHM enterNeighborhoodMode] ⚠️ NO OFFMARKET MARKERS! This likely means the tmk_status migration (016) has not been run.`);
                }
                // Log sample markers for each status
                const statuses = ['ForSale', 'Pending', 'RecentlySold', 'OffMarket'];
                for (const status of statuses) {
                    const sample = markers.filter(m => m.status === status).slice(0, 2);
                    if (sample.length > 0) {
                        console.log(`[NHM enterNeighborhoodMode] Sample ${status} markers:`, sample.map(m => ({
                            tmk: m.tmk,
                            lat: m.lat,
                            lng: m.lng,
                            listPrice: m.listPrice,
                            salePrice: m.salePrice,
                        })));
                    }
                }
                console.log(`[NHM enterNeighborhoodMode] ====================================`);
            })
                .catch(error => {
                console.error('[NHM] Failed to load markers:', error);
                this.state.neighborhoodMode.tmkStatusMarkers = [];
            });
        }
        else {
            console.warn('[NHM] No onLoadMarkers callback provided - NHM will have no markers');
            dataPromise = Promise.resolve();
        }
        // Wait for markers first (fast), then show them immediately
        // Buildings load in background and appear when ready
        await dataPromise;
        console.log(`[NHM PERF] Markers loaded at ${(performance.now() - perfStart).toFixed(0)}ms`);
        // Show property markers at precise locations (rendered last = on top)
        const renderStart = performance.now();
        await this.showNeighborhoodPropertyMarkers(neighborhoodId);
        console.log(`[NHM PERF] Marker rendering took ${(performance.now() - renderStart).toFixed(0)}ms`);
        // Update neighborhood markers (hide others)
        this.updateNeighborhoodMarkers();
        console.log(`[NHM PERF] NHM interactive at ${(performance.now() - perfStart).toFixed(0)}ms (buildings loading in background)`);
        // Buildings will appear when ready (don't block UI)
        buildingsPromise.then(() => {
            console.log(`[NHM PERF] Buildings loaded at ${(performance.now() - perfStart).toFixed(0)}ms`);
        });
    }
    /**
     * Finalize neighborhood mode entry after map has settled
     * Called by the idle event handler after fitBounds/setZoom complete
     */
    finalizeNeighborhoodModeEntry() {
        console.log('[NHM] Finalizing entry - map is idle');
        // Clear the entry guard flag
        this.enteringNeighborhoodMode = false;
        // Update floating label position now that map bounds are stable
        this.updateFloatingLabelPosition();
        // Initialize viewport height tracking for zoom direction detection
        const mapBounds = this.state.map?.getBounds();
        if (mapBounds) {
            this.previousViewportHeight = mapBounds.getNorthEast().lat() - mapBounds.getSouthWest().lat();
        }
    }
    /**
     * Exit neighborhood mode
     */
    exitNeighborhoodMode() {
        // Clear the entry guard flag in case exit is called during entry
        this.enteringNeighborhoodMode = false;
        if (!this.state.neighborhoodMode.active)
            return;
        // Clear property markers
        for (const marker of this.state.neighborhoodMode.propertyMarkers) {
            marker.map = null;
        }
        this.state.neighborhoodMode.propertyMarkers = [];
        // Clear building markers
        for (const marker of this.state.neighborhoodMode.buildingMarkers) {
            marker.map = null;
        }
        this.state.neighborhoodMode.buildingMarkers = [];
        // Clear building polygons
        for (const polygon of this.state.neighborhoodMode.buildingPolygons) {
            polygon.setMap(null);
        }
        this.state.neighborhoodMode.buildingPolygons = [];
        // Clear building floor lines
        for (const line of this.state.neighborhoodMode.buildingFloorLines) {
            line.setMap(null);
        }
        this.state.neighborhoodMode.buildingFloorLines = [];
        this.state.neighborhoodMode.buildingSource = [];
        // Remove floating label
        if (this.state.neighborhoodMode.floatingLabel) {
            this.state.neighborhoodMode.floatingLabel.remove();
            this.state.neighborhoodMode.floatingLabel = null;
        }
        // Remove mode polygon
        if (this.state.neighborhoodMode.polygon) {
            this.state.neighborhoodMode.polygon.setMap(null);
            this.state.neighborhoodMode.polygon = null;
        }
        // Capture before resetting state below - computeNeighborhoodPolygonVisible
        // (called via applyNeighborhoodPolygonState further down) checks
        // neighborhoodMode.active/neighborhoodId to decide whether a polygon is
        // "the currently active NHM neighborhood" and should stay hidden. Those
        // flags have to be cleared *before* that check runs, not after - doing
        // it after (as this used to) meant the exited neighborhood always still
        // looked like the active NHM one to that check, so it stayed hidden
        // regardless of the real Outline/Zoning/Market Trends setting.
        const exitingNeighborhood = this.state.neighborhoodMode.neighborhood;
        // Reset state
        this.state.neighborhoodMode.active = false;
        this.state.neighborhoodMode.neighborhoodId = null;
        this.state.neighborhoodMode.neighborhood = null;
        this.state.neighborhoodMode.polygonBounds = null;
        this.state.neighborhoodMode.tmkStatusMarkers = [];
        this.state.neighborhoodMode.matchingForSaleCount = 0;
        // Restore the exited neighborhood's regular polygon - whatever style/
        // visibility currently applies (Outline, Zoning, or Market Trends; this
        // used to only ever check Zoning, which meant exiting NHM while Market
        // Trends was on silently dropped that neighborhood's fill for good).
        if (exitingNeighborhood) {
            this.applyNeighborhoodPolygonState(exitingNeighborhood);
        }
        // Reset viewport height tracking and clear any pending exit check
        this.previousViewportHeight = null;
        if (this.neighborhoodModeExitDebounceTimer) {
            clearTimeout(this.neighborhoodModeExitDebounceTimer);
            this.neighborhoodModeExitDebounceTimer = null;
        }
        // Update neighborhood markers (show all again)
        this.updateNeighborhoodMarkers();
        // Emit exit event
        if (this.options.onNeighborhoodModeExit) {
            this.options.onNeighborhoodModeExit();
        }
    }
    /**
     * Check if we should exit neighborhood mode based on polygon size in viewport
     * Exits when polygon height is less than 50% of viewport height
     * Only triggers exit when zooming OUT (viewport getting larger)
     */
    checkNeighborhoodModeExit() {
        if (!this.state.map || !this.state.neighborhoodMode.polygonBounds)
            return;
        const mapBounds = this.state.map.getBounds();
        if (!mapBounds)
            return;
        const polygonBounds = this.state.neighborhoodMode.polygonBounds;
        // Calculate polygon height in latitude degrees
        const polygonHeight = polygonBounds.getNorthEast().lat() - polygonBounds.getSouthWest().lat();
        // Calculate viewport height in latitude degrees
        const viewportHeight = mapBounds.getNorthEast().lat() - mapBounds.getSouthWest().lat();
        // Check if we're zooming OUT (viewport getting larger = seeing more of the map)
        // This prevents false exits during zoom-in gestures on mobile
        const isZoomingOut = this.previousViewportHeight !== null && viewportHeight > this.previousViewportHeight;
        // Update tracking for next check
        this.previousViewportHeight = viewportHeight;
        // Only exit when zooming OUT and polygon becomes too small in viewport
        if (isZoomingOut && polygonHeight < viewportHeight * 0.5) {
            this.exitNeighborhoodMode();
        }
    }
    /**
     * Check if neighborhood mode is active
     */
    isNeighborhoodModeActive() {
        return this.state.neighborhoodMode.active;
    }
    /**
     * Get the current neighborhood mode ID
     */
    getNeighborhoodModeId() {
        return this.state.neighborhoodMode.neighborhoodId;
    }
    /**
     * Refresh neighborhood property markers (call when filter settings change)
     * This clears and rebuilds the property markers in neighborhood mode
     */
    async refreshNeighborhoodMarkers() {
        if (!this.state.neighborhoodMode.active || !this.state.neighborhoodMode.neighborhoodId)
            return;
        console.log(`[NHM refreshNeighborhoodMarkers] Refreshing markers, activeStatuses:`, Array.from(this.state.activeStatuses));
        // Clear existing property markers
        for (const marker of this.state.neighborhoodMode.propertyMarkers) {
            marker.map = null;
        }
        this.state.neighborhoodMode.propertyMarkers = [];
        // Rebuild with current settings
        await this.showNeighborhoodPropertyMarkers(this.state.neighborhoodMode.neighborhoodId);
    }
    /**
     * Show property markers at precise TMK locations in neighborhood mode
     */
    async showNeighborhoodPropertyMarkers(_neighborhoodId) {
        if (!this.state.map)
            return;
        const { AdvancedMarkerElement } = await this.getMarkerLibrary();
        const zoom = this.state.map.getZoom() ?? DEFAULT_ZOOM;
        const showPrices = zoom >= SlowMauiMap.PRICE_LABEL_ZOOM_THRESHOLD;
        // PRIORITY 1: Use tmkStatusMarkers from new /listings/markers endpoint
        const tmkStatusMarkers = this.state.neighborhoodMode.tmkStatusMarkers;
        if (tmkStatusMarkers.length > 0) {
            console.log(`[NHM showNeighborhoodPropertyMarkers] Using ${tmkStatusMarkers.length} tmkStatusMarkers`);
            await this.showTmkStatusMarkers(tmkStatusMarkers, showPrices, zoom, AdvancedMarkerElement);
            // Update floating label badge with matching count
            this.updateFloatingLabelBadge();
        }
        else {
            console.log(`[NHM showNeighborhoodPropertyMarkers] No markers available`);
        }
    }
    /**
     * TmkStatusMarker equivalent of matchesActiveAttribute - same "Status OR
     * Attribute" rule as the generic listing layer, applied to NHM mode's
     * own marker set so turning For Sale off inside a neighborhood doesn't
     * hide a New/Open House/Favorited/Alerted/Price-Changed property either.
     */
    tmkMarkerMatchesActiveAttribute(marker) {
        return this.matchesAnyActiveAttribute(factsFromTmkStatusMarker(marker));
    }
    /**
     * Check if a marker matches the current property filters
     */
    markerMatchesFilters(m) {
        const filters = this.state.propertyFilters;
        const allTypes = ['house', 'condo', 'land'];
        const selectedTypes = filters.propertyTypes ?? [];
        const typeFilterActive = selectedTypes.length > 0 && selectedTypes.length < allTypes.length;
        const hasFilters = filters.minPrice !== undefined || filters.maxPrice !== undefined ||
            filters.minBedrooms !== undefined || filters.minBathrooms !== undefined ||
            filters.minYearBuilt !== undefined || filters.minSqft !== undefined ||
            filters.maxSqft !== undefined || filters.minDaysOnMarket !== undefined ||
            typeFilterActive;
        if (!hasFilters)
            return true;
        // Price filter - use listPrice for ForSale/Pending, salePrice for RecentlySold
        const price = m.listPrice || m.salePrice || 0;
        if (filters.minPrice !== undefined && price > 0 && price < filters.minPrice) {
            return false;
        }
        if (filters.maxPrice !== undefined && price > 0 && price > filters.maxPrice) {
            return false;
        }
        // Bedrooms/bathrooms/propertyType ARE on TmkStatusMarker (migration
        // 040) and were already declared on NHMPropertyFilters/threaded
        // through from main.ts's Filters drawer, but never actually checked
        // here - changing Beds/Baths/Type while in Neighborhood Mode silently
        // had zero effect on the map, while the sidebar (which runs through
        // filterAndSortListings() in main.ts instead) filtered correctly.
        if (filters.minBedrooms !== undefined && (m.bedroomsTotal ?? 0) < filters.minBedrooms) {
            return false;
        }
        if (filters.minBathrooms !== undefined && (m.bathroomsTotal ?? 0) < filters.minBathrooms) {
            return false;
        }
        if (filters.minYearBuilt !== undefined && (m.yearBuilt ?? 0) < filters.minYearBuilt) {
            return false;
        }
        if (filters.minSqft !== undefined && (m.livingArea ?? 0) < filters.minSqft) {
            return false;
        }
        if (filters.maxSqft !== undefined && (m.livingArea === undefined || m.livingArea > filters.maxSqft)) {
            return false;
        }
        if (filters.minDaysOnMarket !== undefined && (m.daysOnMarket ?? 0) < filters.minDaysOnMarket) {
            return false;
        }
        if (typeFilterActive) {
            // Same Residential/Condominium/Land -> house/condo/land mapping as
            // filterAndSortListings() in apps/public/main.ts - no shared
            // classifier to call instead, so keep these in sync by hand.
            const type = (m.propertyType || '').toLowerCase();
            const matchesType = (selectedTypes.includes('house') && type === 'residential') ||
                (selectedTypes.includes('condo') && type === 'condominium') ||
                (selectedTypes.includes('land') && type === 'land');
            if (!matchesType)
                return false;
        }
        return true;
    }
    /**
     * Show markers from tmk_status table (pre-computed status and badges)
     * v1-style: filtered-out properties show dimmed instead of hidden
     */
    async showTmkStatusMarkers(markers, showPrices, zoom, AdvancedMarkerElement) {
        if (!this.state.map)
            return;
        // Map CNTL active statuses to NHM statuses for filtering
        const activeNHMStatuses = new Set();
        if (this.state.activeStatuses.has('Active'))
            activeNHMStatuses.add('ForSale');
        if (this.state.activeStatuses.has('Pending'))
            activeNHMStatuses.add('Pending');
        if (this.state.activeStatuses.has('Sold'))
            activeNHMStatuses.add('RecentlySold');
        if (this.state.activeStatuses.has('OffMarket'))
            activeNHMStatuses.add('OffMarket');
        console.log(`[NHM showTmkStatusMarkers] ========== RENDERING MARKERS ==========`);
        console.log(`[NHM showTmkStatusMarkers] Total markers available: ${markers.length}`);
        console.log(`[NHM showTmkStatusMarkers] Active CNTL statuses: [${Array.from(this.state.activeStatuses).join(', ')}]`);
        console.log(`[NHM showTmkStatusMarkers] Mapped to NHM statuses: [${Array.from(activeNHMStatuses).join(', ')}]`);
        console.log(`[NHM showTmkStatusMarkers] Property filters:`, this.state.propertyFilters);
        // Separate markers into matching vs dimmed (v1 style - show dimmed instead of hiding)
        const matchingMarkers = [];
        const dimmedMarkers = [];
        for (const m of markers) {
            if (this.markerMatchesFilters(m)) {
                matchingMarkers.push(m);
            }
            else {
                dimmedMarkers.push(m);
            }
        }
        console.log(`[NHM showTmkStatusMarkers] Matching filters: ${matchingMarkers.length}, Dimmed: ${dimmedMarkers.length}`);
        // Count matching ForSale markers for neighborhood label
        const matchingForSaleCount = matchingMarkers.filter(m => m.status === 'ForSale').length;
        this.state.neighborhoodMode.matchingForSaleCount = matchingForSaleCount;
        // Log breakdown of markers by status
        const allStatusCounts = { ForSale: 0, Pending: 0, RecentlySold: 0, OffMarket: 0 };
        for (const m of matchingMarkers) {
            allStatusCounts[m.status] = (allStatusCounts[m.status] || 0) + 1;
        }
        console.log(`[NHM showTmkStatusMarkers] Matching markers by status:`, allStatusCounts);
        // Render order: Off-Market (back) → Recently Sold → Pending → For Sale (front)
        const renderOrder = ['OffMarket', 'RecentlySold', 'Pending', 'ForSale'];
        let totalRendered = 0;
        // First render DIMMED markers (lower z-index, in back)
        for (const status of renderOrder) {
            const statusMarkers = dimmedMarkers.filter(m => m.status === status);
            const isActive = activeNHMStatuses.has(status);
            // Status OR Attribute - a marker whose status isn't toggled on can
            // still show if it matches an active attribute filter.
            const visibleMarkers = isActive ? statusMarkers : statusMarkers.filter(m => this.tmkMarkerMatchesActiveAttribute(m));
            if (visibleMarkers.length === 0)
                continue;
            for (const tmkMarker of visibleMarkers) {
                const color = NHM_DIMMED_COLORS[status];
                // Don't show price labels on dimmed markers
                const content = this.createTmkStatusMarkerContent(tmkMarker, color, undefined, zoom, true);
                const marker = new AdvancedMarkerElement({
                    position: { lat: tmkMarker.lat, lng: tmkMarker.lng },
                    map: this.state.map,
                    content,
                    title: this.getTmkStatusMarkerTitle(tmkMarker),
                    zIndex: 50, // Lower z-index for dimmed
                });
                marker.tmkStatusMarkerData = tmkMarker;
                marker.isDimmed = true;
                content.addEventListener('click', () => {
                    console.log('[NHM] TmkStatus marker clicked (dimmed):', tmkMarker);
                    if (this.options.onTmkStatusMarkerClick) {
                        this.options.onTmkStatusMarkerClick(tmkMarker);
                    }
                });
                this.state.neighborhoodMode.propertyMarkers.push(marker);
                totalRendered++;
            }
        }
        // Then render MATCHING markers (higher z-index, in front)
        for (const status of renderOrder) {
            const statusMarkers = matchingMarkers.filter(m => m.status === status);
            const isActive = activeNHMStatuses.has(status);
            // Status OR Attribute - see tmkMarkerMatchesActiveAttribute.
            const visibleMarkers = isActive ? statusMarkers : statusMarkers.filter(m => this.tmkMarkerMatchesActiveAttribute(m));
            console.log(`[NHM showTmkStatusMarkers] Status "${status}": ${statusMarkers.length} matching markers, active=${isActive}, visible=${visibleMarkers.length}`);
            for (const tmkMarker of visibleMarkers) {
                const color = NHM_STATUS_COLORS[status];
                const priceLabel = showPrices && tmkMarker.listPrice
                    ? this.formatPriceLabel(tmkMarker.listPrice)
                    : showPrices && tmkMarker.salePrice
                        ? this.formatPriceLabel(tmkMarker.salePrice)
                        : undefined;
                const content = this.createTmkStatusMarkerContent(tmkMarker, color, priceLabel, zoom, false);
                const marker = new AdvancedMarkerElement({
                    position: { lat: tmkMarker.lat, lng: tmkMarker.lng },
                    map: this.state.map,
                    content,
                    title: this.getTmkStatusMarkerTitle(tmkMarker),
                    zIndex: 75, // Higher than dimmed (50) but lower than neighborhood markers (200)
                });
                marker.tmkStatusMarkerData = tmkMarker;
                content.addEventListener('click', () => {
                    console.log('[NHM] TmkStatus marker clicked:', tmkMarker);
                    if (this.options.onTmkStatusMarkerClick) {
                        this.options.onTmkStatusMarkerClick(tmkMarker);
                    }
                });
                this.state.neighborhoodMode.propertyMarkers.push(marker);
                totalRendered++;
            }
        }
        console.log(`[NHM showTmkStatusMarkers] Total markers rendered: ${totalRendered}`);
        console.log(`[NHM showTmkStatusMarkers] =========================================`);
    }
    /**
     * Create marker content for TmkStatusMarker
     * Includes badges for isNew, hasOpenHouse, hasPriceChange
     * @param dimmed - If true, marker appears faded (filtered out but still visible)
     */
    createTmkStatusMarkerContent(tmkMarker, color, priceLabel, zoom, dimmed = false) {
        // Circle size based on zoom (slightly smaller for dimmed)
        let circleSize;
        let priceSize;
        if (zoom >= 19) {
            circleSize = dimmed ? 14 : 20;
            priceSize = 12;
        }
        else if (zoom >= 18) {
            circleSize = dimmed ? 12 : 18;
            priceSize = 11;
        }
        else if (zoom >= 17) {
            circleSize = dimmed ? 10 : 16;
            priceSize = 9;
        }
        else if (zoom >= 15) {
            circleSize = dimmed ? 8 : 14;
            priceSize = 0;
        }
        else {
            circleSize = dimmed ? 6 : 12;
            priceSize = 0;
        }
        // Container sized to circle only - price/badges positioned absolutely
        // This ensures the circle center is always at the lat/lng position
        const content = document.createElement('div');
        content.style.cssText = `
      position: relative;
      width: ${circleSize}px;
      height: ${circleSize}px;
      cursor: pointer;
      ${dimmed ? 'opacity: 0.6;' : ''}
    `;
        // Experimental: fade the circle and write the unit number across it
        // instead of a plain gray dot - only when there's a real unit number
        // and the circle is large enough for the text to actually be legible.
        // Falls back to the normal circle otherwise, and is fully gated behind
        // offMarketUnitLabelsEnabled (see setOffMarketUnitLabelsEnabled) so it
        // can be reverted with a single flag flip if it doesn't work out.
        const showUnitLabel = this.state.offMarketUnitLabelsEnabled &&
            tmkMarker.status === 'OffMarket' &&
            !dimmed &&
            !!tmkMarker.unitName &&
            circleSize >= 16;
        const circle = document.createElement('div');
        circle.style.cssText = `
      width: ${circleSize}px;
      height: ${circleSize}px;
      background: ${color};
      border-radius: 50%;
      border: ${dimmed ? '1px' : '2px'} solid ${dimmed ? '#e0e0e0' : '#fff'};
      box-shadow: ${dimmed ? '0 0 2px rgba(0,0,0,0.1)' : '0 1px 3px rgba(0,0,0,0.3)'};
      ${showUnitLabel ? 'opacity: 0.2;' : ''}
      transition: transform 120ms ease, box-shadow 120ms ease;
    `;
        content.appendChild(circle);
        if (showUnitLabel) {
            const unitLabel = document.createElement('div');
            unitLabel.style.cssText = `
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: ${Math.max(8, Math.round(circleSize * 0.4))}px;
        font-weight: 700;
        color: #4b5563;
        text-shadow: 0 1px 1px rgba(255, 255, 255, 0.85);
        white-space: nowrap;
        cursor: pointer;
      `;
            unitLabel.textContent = tmkMarker.unitName;
            content.appendChild(unitLabel);
        }
        // Price label positioned above circle (doesn't affect circle centering)
        if (priceLabel && priceSize > 0) {
            const label = document.createElement('div');
            label.style.cssText = `
        position: absolute;
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%);
        margin-bottom: 2px;
        background: white;
        padding: 2px 4px;
        border-radius: 3px;
        font-size: ${priceSize}px;
        font-weight: 600;
        color: #333;
        white-space: nowrap;
        box-shadow: 0 1px 2px rgba(0,0,0,0.2);
      `;
            label.textContent = priceLabel;
            content.appendChild(label);
        }
        // Add badges (horizontal row below circle)
        const badges = this.renderTmkStatusBadges(tmkMarker, circleSize);
        if (badges) {
            content.insertAdjacentHTML('beforeend', badges);
        }
        return content;
    }
    /**
     * Render badges for TmkStatusMarker as horizontal row below marker
     */
    renderTmkStatusBadges(tmkMarker, _markerSize) {
        const isDesktop = window.innerWidth >= 1024;
        const scale = isDesktop ? 1.5 : 1;
        const badgeSize = Math.round(BADGE_SIZE_BASE * scale);
        const facts = factsFromTmkStatusMarker(tmkMarker);
        const activeBadges = this.activeBadgeEntries((entry) => entry.matches(facts, this.attributeIdSets));
        return this.renderBadgeRow(activeBadges, badgeSize);
    }
    /**
     * Get title for TmkStatusMarker tooltip
     */
    getTmkStatusMarkerTitle(marker) {
        const parts = [];
        if (marker.listPrice) {
            parts.push(this.formatPriceLabel(marker.listPrice));
        }
        else if (marker.salePrice) {
            parts.push(`Sold: ${this.formatPriceLabel(marker.salePrice)}`);
        }
        if (marker.unitName) {
            parts.push(marker.unitName);
        }
        else if (marker.address) {
            parts.push(marker.address);
        }
        if (parts.length === 0) {
            parts.push(marker.status);
        }
        return parts.join(' - ');
    }
    /**
     * Create floating label for neighborhood mode
     * Positioned above the polygon's top edge, clamped to viewport top
     */
    injectNeighborhoodLabelAttentionStyles() {
        if (document.getElementById(NEIGHBORHOOD_LABEL_ATTENTION_STYLE_ID))
            return;
        const style = document.createElement('style');
        style.id = NEIGHBORHOOD_LABEL_ATTENTION_STYLE_ID;
        style.textContent = NEIGHBORHOOD_LABEL_ATTENTION_CSS;
        document.head.appendChild(style);
    }
    createFloatingLabel(neighborhood) {
        if (!this.state.map)
            return;
        this.injectNeighborhoodLabelAttentionStyles();
        // Remove existing floating label if any
        if (this.state.neighborhoodMode.floatingLabel) {
            this.state.neighborhoodMode.floatingLabel.remove();
        }
        // Create floating label element
        const label = document.createElement('div');
        label.className = 'neighborhood-floating-label';
        label.style.cssText = `
      position: absolute;
      z-index: 1000;
      background: rgba(44, 85, 48, 0.95);
      color: white;
      padding: 8px 16px;
      border-radius: 8px;
      font-size: 1rem;
      font-weight: 600;
      white-space: nowrap;
      cursor: pointer;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
      pointer-events: auto;
      transform: translateX(-50%);
      transition: background 0.2s, box-shadow 0.2s, opacity 0.2s;
    `;
        // Container for name + dropdown arrow
        const nameContainer = document.createElement('span');
        nameContainer.innerHTML = `
      <span>${this.escapeHtml(formatNeighborhoodName(neighborhood.name))}</span>
      <span style="margin-left: 8px; font-size: 0.8rem; opacity: 0.8;">▾</span>
    `;
        label.appendChild(nameContainer);
        // Badge for matching ForSale count (v1 style - positioned to the right)
        const badge = document.createElement('span');
        badge.className = 'floating-label-badge';
        badge.style.cssText = `
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: #22c55e;
      color: white;
      font-size: 0.75rem;
      font-weight: 700;
      min-width: 20px;
      height: 20px;
      padding: 0 6px;
      border-radius: 10px;
      margin-left: 10px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
    `;
        badge.textContent = '0';
        label.appendChild(badge);
        // "More info" line - the pulse animation draws the eye but doesn't say
        // what clicking actually does; this spells it out. Block-level so it
        // falls on its own line under the name+arrow+badge row above (those
        // three stay inline/same-line) rather than needing its own position.
        // Plain text, not a separate interactive element - the whole label
        // already shares one click handler (see below).
        const moreInfoLine = document.createElement('div');
        moreInfoLine.textContent = 'More info';
        moreInfoLine.style.cssText = `
      font-size: 0.7rem;
      font-weight: 400;
      text-decoration: underline;
      opacity: 0.85;
      margin-top: 2px;
    `;
        label.appendChild(moreInfoLine);
        // Hover effect
        label.addEventListener('mouseenter', () => {
            label.style.background = 'rgba(44, 85, 48, 1)';
            label.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.4)';
        });
        label.addEventListener('mouseleave', () => {
            const isDimmed = label.classList.contains('dimmed');
            label.style.background = isDimmed ? 'rgba(107, 142, 112, 0.8)' : 'rgba(44, 85, 48, 0.95)';
            label.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.3)';
        });
        // Click handler - open neighborhood modal
        label.addEventListener('click', () => {
            if (this.options.onFloatingLabelClick && this.state.neighborhoodMode.neighborhood) {
                this.options.onFloatingLabelClick(this.state.neighborhoodMode.neighborhood);
            }
        });
        // Add to map container
        const mapDiv = this.state.map.getDiv();
        mapDiv.appendChild(label);
        this.state.neighborhoodMode.floatingLabel = label;
        // Draw the eye to the label for a couple seconds right after it shows up
        // - it's the only way in for the first-time visitor and gives no other
        // affordance that it's clickable besides the dropdown arrow. Stops on
        // its own once the animation finishes (3 iterations) rather than
        // lingering, so it reads as "notice me" rather than a nagging loop.
        label.classList.add('neighborhood-floating-label--attention');
        label.addEventListener('animationend', () => label.classList.remove('neighborhood-floating-label--attention'), { once: true });
        // Initial position update
        this.updateFloatingLabelPosition();
    }
    /**
     * Update floating label badge count and dimmed state
     */
    updateFloatingLabelBadge() {
        const label = this.state.neighborhoodMode.floatingLabel;
        if (!label)
            return;
        const count = this.state.neighborhoodMode.matchingForSaleCount;
        const badge = label.querySelector('.floating-label-badge');
        if (badge) {
            badge.textContent = count.toString();
            badge.style.display = count > 0 ? 'inline-flex' : 'none';
        }
        // Dim the label if no matching ForSale properties
        const isDimmed = count === 0;
        if (isDimmed) {
            label.classList.add('dimmed');
            label.style.background = 'rgba(107, 142, 112, 0.8)';
            label.style.opacity = '0.7';
        }
        else {
            label.classList.remove('dimmed');
            label.style.background = 'rgba(44, 85, 48, 0.95)';
            label.style.opacity = '1';
        }
    }
    /**
     * Update floating label position based on map projection
     * Label is positioned above the polygon's top edge, clamped to min 110px from viewport top
     */
    updateFloatingLabelPosition() {
        if (!this.state.map || !this.state.neighborhoodMode.floatingLabel || !this.state.neighborhoodMode.polygonBounds)
            return;
        const label = this.state.neighborhoodMode.floatingLabel;
        const polygonBounds = this.state.neighborhoodMode.polygonBounds;
        // Get the map viewport bounds
        const viewportBounds = this.state.map.getBounds();
        if (!viewportBounds)
            return;
        const ne = viewportBounds.getNorthEast();
        const sw = viewportBounds.getSouthWest();
        // Get the map div dimensions
        const mapDiv = this.state.map.getDiv();
        const mapWidth = mapDiv.offsetWidth;
        const mapHeight = mapDiv.offsetHeight;
        // Calculate the position as a fraction of the viewport
        const latRange = ne.lat() - sw.lat();
        const lngRange = ne.lng() - sw.lng();
        // Get polygon's top-center point (north edge, center longitude)
        const polygonTopLat = polygonBounds.getNorthEast().lat();
        const polygonCenterLng = polygonBounds.getCenter().lng();
        // Convert polygon top-center to pixel coordinates
        const pixelX = ((polygonCenterLng - sw.lng()) / lngRange) * mapWidth;
        const pixelY = ((ne.lat() - polygonTopLat) / latRange) * mapHeight;
        // Get label dimensions
        const labelWidth = label.offsetWidth;
        const labelHeight = label.offsetHeight;
        // Position label above polygon top (with offset)
        const desiredTop = pixelY - FLOATING_LABEL_OFFSET - labelHeight;
        // Clamp to minimum distance from viewport top
        const actualTop = Math.max(FLOATING_LABEL_MIN_TOP, desiredTop);
        // Clamp horizontal position to keep label within viewport
        const padding = 10;
        const desiredLeft = pixelX;
        const minLeft = labelWidth / 2 + padding;
        const maxLeft = mapWidth - labelWidth / 2 - padding;
        const actualLeft = Math.max(minLeft, Math.min(desiredLeft, maxLeft));
        // Position the label
        label.style.left = `${actualLeft}px`;
        label.style.top = `${actualTop}px`;
        // Always show when in neighborhood mode (clamping keeps it visible)
        label.style.display = 'block';
    }
    /**
     * Load and show building markers/polygons in neighborhood mode
     */
    async loadAndShowBuildings(neighborhoodId) {
        if (!this.state.map || !this.options.onLoadBuildings)
            return;
        try {
            const buildings = await this.options.onLoadBuildings(neighborhoodId);
            if (!buildings || buildings.length === 0)
                return;
            const source = buildings.map((building) => {
                let footprint = [];
                if (building.polygon && Array.isArray(building.polygon) && building.polygon.length > 2) {
                    const validPolygon = building.polygon.every((p) => p && typeof p.lat === 'number' && typeof p.lng === 'number' &&
                        isFinite(p.lat) && isFinite(p.lng));
                    if (validPolygon)
                        footprint = building.polygon;
                }
                // Calculate building center (used to anchor the label)
                let lat = building.lat;
                let lng = building.lng;
                if ((!lat || !lng) && footprint.length > 0) {
                    const sumLat = footprint.reduce((sum, p) => sum + p.lat, 0);
                    const sumLng = footprint.reduce((sum, p) => sum + p.lng, 0);
                    lat = sumLat / footprint.length;
                    lng = sumLng / footprint.length;
                }
                return {
                    name: building.name,
                    footprint,
                    anchor: lat && lng ? { lat, lng } : null,
                    floors: building.floors,
                };
            });
            this.state.neighborhoodMode.buildingSource = source;
            await this.redrawBuildingOverlays();
        }
        catch (err) {
            console.error('Failed to load buildings:', err);
        }
    }
    /**
     * Assumed floor count used only for the extrusion effect's height, when a
     * building has no `floors` on record. Rendering-only — must never be
     * written back to the buildings table or surfaced as factual copy.
     */
    effectiveBuildingFloors(floors) {
        const f = floors == null ? BUILDING_EXTRUSION_STUB_FLOORS : floors;
        if (f <= BUILDING_EXTRUSION_CAP_FLOORS)
            return f;
        return BUILDING_EXTRUSION_CAP_FLOORS + Math.sqrt(f - BUILDING_EXTRUSION_CAP_FLOORS);
    }
    /**
     * (Re)draw neighborhood-mode building footprints from state.buildingSource,
     * flat or extruded depending on state.buildingExtrusionEnabled. Called once
     * on load and again on every pan/zoom settle (via handleBoundsChanged) —
     * the extrusion offset is computed in screen pixels, so it isn't valid
     * across zoom levels the way the flat lat/lng footprint is.
     */
    async redrawBuildingOverlays() {
        const mode = this.state.neighborhoodMode;
        if (!this.state.map || !mode.active || mode.buildingSource.length === 0)
            return;
        for (const polygon of mode.buildingPolygons)
            polygon.setMap(null);
        mode.buildingPolygons = [];
        for (const line of mode.buildingFloorLines)
            line.setMap(null);
        mode.buildingFloorLines = [];
        for (const marker of mode.buildingMarkers)
            marker.map = null;
        mode.buildingMarkers = [];
        const map = this.state.map;
        const extrude = this.state.buildingExtrusionEnabled;
        // Buildings read as overwhelming on the plain roadmap background (their
        // fills have much more contrast against flat pale map tiles than against
        // busy satellite/hybrid imagery), so halve fill opacity in roadmap mode
        // only. Lines/strokes are unaffected.
        const fillOpacityScale = map.getMapTypeId() === 'roadmap' ? 0.5 : 1;
        // Screen-space pixel <-> lat/lng conversion, using the same linear
        // viewport-fraction approximation as updateFloatingLabelPosition() above
        // (accurate enough at neighborhood-mode zoom levels, no projection/
        // OverlayView plumbing required).
        let toPixel = null;
        let toLatLng = null;
        if (extrude) {
            const viewportBounds = map.getBounds();
            const mapDiv = map.getDiv();
            const mapWidth = mapDiv.offsetWidth;
            const mapHeight = mapDiv.offsetHeight;
            if (viewportBounds && mapWidth && mapHeight) {
                const ne = viewportBounds.getNorthEast();
                const sw = viewportBounds.getSouthWest();
                const latRange = ne.lat() - sw.lat();
                const lngRange = ne.lng() - sw.lng();
                toPixel = (p) => ({
                    x: ((p.lng - sw.lng()) / lngRange) * mapWidth,
                    y: ((ne.lat() - p.lat) / latRange) * mapHeight,
                });
                toLatLng = (p) => ({
                    lat: ne.lat() - (p.y / mapHeight) * latRange,
                    lng: sw.lng() + (p.x / mapWidth) * lngRange,
                });
            }
        }
        const { AdvancedMarkerElement } = await this.getMarkerLibrary();
        for (const building of mode.buildingSource) {
            let labelVec = { dx: 0, dy: 0 };
            if (building.footprint.length > 2) {
                if (extrude && toPixel && toLatLng) {
                    const eff = this.effectiveBuildingFloors(building.floors);
                    // Scale the extrusion by the same zoom-doubling factor the map
                    // itself uses, anchored to BUILDING_EXTRUSION_REFERENCE_ZOOM (the
                    // zoom BUILDING_EXTRUSION_PX_PER_FLOOR was tuned at) — the
                    // footprint scales with zoom (via toPixel) but a fixed-pixel
                    // extrusion doesn't, so it reads as oversized relative to a
                    // zoomed-out (shrunk) footprint. Uncapped: grows past the tuned
                    // value zoomed in past the reference zoom, shrinks below it
                    // zoomed out, matching how the footprint itself scales.
                    const currentZoom = this.state.map.getZoom() ?? BUILDING_EXTRUSION_REFERENCE_ZOOM;
                    const zoomScale = Math.pow(2, currentZoom - BUILDING_EXTRUSION_REFERENCE_ZOOM);
                    const len = BUILDING_EXTRUSION_PX_PER_FLOOR * eff * zoomScale;
                    const dirLen = Math.hypot(BUILDING_EXTRUSION_DIRECTION.dx, BUILDING_EXTRUSION_DIRECTION.dy);
                    const vec = {
                        dx: (BUILDING_EXTRUSION_DIRECTION.dx / dirLen) * len,
                        dy: (BUILDING_EXTRUSION_DIRECTION.dy / dirLen) * len,
                    };
                    labelVec = vec;
                    const groundPx = building.footprint.map((p) => toPixel(p));
                    const n = groundPx.length;
                    // Normalize winding order: the per-edge outward-normal formula
                    // below only produces correct results for one consistent winding
                    // direction, but building footprints aren't guaranteed to share
                    // one (depends on how each was originally digitized/imported) -
                    // e.g. Polo Beach Club's polygon winds opposite to most other
                    // buildings, which without this flipped every wall's visibility,
                    // showing exactly the sides that should've been hidden and vice
                    // versa. Shoelace signed area: reverse if wound the "wrong" way.
                    let signedArea = 0;
                    for (let i = 0; i < n; i++) {
                        const a = groundPx[i];
                        const b = groundPx[(i + 1) % n];
                        signedArea += a.x * b.y - b.x * a.y;
                    }
                    if (signedArea < 0)
                        groundPx.reverse();
                    // An edge is front-facing (visible) when its outward normal points
                    // away from the extrusion direction. Back-facing edges are the
                    // building's far side, hidden behind its own solid roof/mass from
                    // this viewing angle, so they're culled entirely rather than drawn
                    // and left peeking out past the roof.
                    const visibleEdges = [];
                    for (let i = 0; i < n; i++) {
                        const p1 = groundPx[i];
                        const p2 = groundPx[(i + 1) % n];
                        const nx = p2.y - p1.y;
                        const ny = -(p2.x - p1.x);
                        const facingDot = nx * vec.dx + ny * vec.dy;
                        if (facingDot > 0)
                            continue; // back-facing, hidden behind the roof
                        visibleEdges.push(i);
                    }
                    const realFloors = Math.min(building.floors ?? 1, BUILDING_EXTRUSION_MAX_FLOOR_LINES);
                    // Walls: each visible edge is sliced into one band per floor,
                    // cycling through BUILDING_FLOOR_COLORS, so each story reads as a
                    // distinct color rather than a line through a uniform shadow.
                    // Validated with the dataviz skill's palette validator (adjacent
                    // pairs, light mode): lightness/chroma/CVD-separation/normal-vision
                    // checks all pass; the sub-3:1 surface contrast WARN is mitigated
                    // by the floor-division lines below (visible "relief"), which also
                    // mark the boundary where the 5-color cycle repeats (floor 6 reuses
                    // floor 1's color, so the line is the only cue at that seam).
                    for (const i of visibleEdges) {
                        const p1 = groundPx[i];
                        const p2 = groundPx[(i + 1) % n];
                        for (let level = 0; level < realFloors; level++) {
                            const t0 = level / realFloors;
                            const t1 = (level + 1) / realFloors;
                            const bandPx = [
                                { x: p1.x + vec.dx * t0, y: p1.y + vec.dy * t0 },
                                { x: p2.x + vec.dx * t0, y: p2.y + vec.dy * t0 },
                                { x: p2.x + vec.dx * t1, y: p2.y + vec.dy * t1 },
                                { x: p1.x + vec.dx * t1, y: p1.y + vec.dy * t1 },
                            ];
                            const band = new google.maps.Polygon({
                                paths: bandPx.map((p) => toLatLng(p)),
                                strokeWeight: 0,
                                fillColor: BUILDING_FLOOR_COLORS[level % BUILDING_FLOOR_COLORS.length],
                                fillOpacity: 0.65 * fillOpacityScale,
                                map,
                            });
                            mode.buildingPolygons.push(band);
                        }
                    }
                    // Floor division lines: (realFloors - 1) lines per visible wall,
                    // evenly spaced through the extruded height, so a 3-story building
                    // shows 2 lines through its bands. Buildings with no floors on
                    // record (or exactly 1) get a single flat color, no lines.
                    for (let level = 1; level < realFloors; level++) {
                        const t = level / realFloors;
                        const levelVec = { dx: vec.dx * t, dy: vec.dy * t };
                        for (const i of visibleEdges) {
                            const p1 = groundPx[i];
                            const p2 = groundPx[(i + 1) % n];
                            const line = new google.maps.Polyline({
                                path: [
                                    toLatLng({ x: p1.x + levelVec.dx, y: p1.y + levelVec.dy }),
                                    toLatLng({ x: p2.x + levelVec.dx, y: p2.y + levelVec.dy }),
                                ],
                                strokeColor: BUILDING_FLOOR_LINE_COLOR,
                                strokeWeight: 1,
                                strokeOpacity: 0.35,
                                map,
                            });
                            mode.buildingFloorLines.push(line);
                        }
                    }
                    const roofPx = groundPx.map((p) => ({ x: p.x + vec.dx, y: p.y + vec.dy }));
                    const roof = new google.maps.Polygon({
                        paths: roofPx.map((p) => toLatLng(p)),
                        strokeColor: '#6b7280', // Medium grey stroke
                        strokeWeight: 1,
                        strokeOpacity: 0.5,
                        fillColor: '#d1d5db', // Light grey fill
                        fillOpacity: 0.5 * fillOpacityScale,
                        map,
                    });
                    mode.buildingPolygons.push(roof);
                }
                else {
                    const roof = new google.maps.Polygon({
                        paths: building.footprint,
                        strokeColor: '#6b7280', // Medium grey stroke
                        strokeWeight: 1,
                        strokeOpacity: 0.5,
                        fillColor: '#d1d5db', // Light grey fill
                        fillOpacity: 0.5,
                        map,
                    });
                    mode.buildingPolygons.push(roof);
                }
            }
            // Create building label marker - medium grey text, no background
            if (building.anchor) {
                let labelPos = building.anchor;
                if (extrude && toPixel && toLatLng && (labelVec.dx !== 0 || labelVec.dy !== 0)) {
                    const anchorPx = toPixel(building.anchor);
                    labelPos = toLatLng({ x: anchorPx.x + labelVec.dx, y: anchorPx.y + labelVec.dy });
                }
                const content = document.createElement('div');
                content.style.cssText = `
          color: #9ca3af;
          padding: 2px 4px;
          font-size: 0.5625rem;
          font-weight: 600;
          white-space: nowrap;
        `;
                content.textContent = building.name;
                const marker = new AdvancedMarkerElement({
                    position: labelPos,
                    map,
                    content,
                    title: building.name,
                    zIndex: 50,
                });
                mode.buildingMarkers.push(marker);
            }
        }
    }
    // ==================== COMMON ====================
    /**
     * Batches updateVisibility/updateListingLayer/rescaleGeographyLabels/
     * rescaleNeighborhoodLabels into a single requestAnimationFrame per
     * paint, instead of running all four on every zoom_changed event. With
     * isFractionalZoomEnabled, a pinch gesture fires zoom_changed far more
     * often than the browser paints, so multiple calls queued within the
     * same frame collapse into the one rAF callback below (which always
     * reads the zoom level fresh when it actually runs).
     */
    scheduleZoomTick() {
        if (this.pendingZoomTickRaf !== null)
            return;
        this.pendingZoomTickRaf = requestAnimationFrame(() => {
            this.pendingZoomTickRaf = null;
            this.updateVisibility();
            this.updateListingLayer();
            this.rescaleGeographyLabels();
            this.rescaleNeighborhoodLabels();
            this.hideOutOfScopeNeighborhoodMarkers();
        });
    }
    /**
     * Hides neighborhood count-badge markers that just fell out of scope at
     * the current zoom, immediately - not waiting on updateNeighborhoodMarkers'
     * debounce (which only fires ~100ms after the user stops touching the
     * map entirely, since it resets on every zoom_changed event during a
     * continuous gesture). Without this, updateListingLayer's useUnmapped
     * flip (zoom crossing NEIGHBORHOOD_MARKERS_MIN_ZOOM downward) immediately
     * folds a neighborhood's listings back into the generic cluster bubble -
     * which can then render right next to/over that neighborhood's own
     * count badge, which is still sitting there until the debounce finally
     * fires - a visible double-bubble that can last the whole gesture, not
     * just a quick flash.
     *
     * Deliberately HIDE-only, never creates/shows: there's nothing cheap to
     * show before the debounced rebuild has actually fetched/built a
     * marker's content, so the zoom-in direction (crossing upward, no badge
     * exists yet) is left to that rebuild as before - only the "stale badge
     * needs to disappear" direction benefits from running outside the
     * debounce, since hiding an already-existing marker is just `.map = null`,
     * no rebuild required.
     */
    hideOutOfScopeNeighborhoodMarkers() {
        if (!this.state.map || this.state.neighborhoodMode.active)
            return;
        if (this.state.neighborhoodMarkers.size === 0)
            return;
        const zoom = this.state.map.getZoom() ?? DEFAULT_ZOOM;
        const showAllMarkers = this.state.neighborhoodsVisible && zoom >= NEIGHBORHOOD_MARKERS_MIN_ZOOM;
        if (showAllMarkers)
            return;
        const forceIconOnlyIds = this.directlyAttributedNeighborhoodIds();
        for (const [id, marker] of this.state.neighborhoodMarkers) {
            if (!forceIconOnlyIds.has(id) && marker.map !== null) {
                marker.map = null;
            }
        }
    }
    updateVisibility() {
        if (!this.state.map)
            return;
        const zoom = this.state.map.getZoom() ?? DEFAULT_ZOOM;
        // When neighborhood markers are showing (zoom >= 13.5 with neighborhoods ON),
        // hide old labels/badges since the new markers replace them
        const showNeighborhoodMarkers = this.state.neighborhoodsVisible && zoom >= NEIGHBORHOOD_MARKERS_MIN_ZOOM;
        // Only show old labels/badges when neighborhood markers are NOT showing.
        // Both also need their own toggle gate, not just "the new layer isn't
        // showing" - `!showNeighborhoodMarkers` is also true whenever
        // neighborhoodsVisible itself is OFF (Labels toggle off), which used to
        // let this legacy plain-text label layer render anyway at zoom >= 14 -
        // exactly the "black text, no box" bug. Gating on neighborhoodsVisible
        // here makes showLabels correctly false in every reachable state (at
        // zoom >= NEIGHBORHOOD_MARKERS_MIN_ZOOM with neighborhoodsVisible true,
        // showNeighborhoodMarkers is already true too - both conditions read
        // the same threshold now), so this old text-label path is fully
        // retired rather than patched, regardless of where that threshold is
        // tuned to.
        const showLabels = this.state.neighborhoodsVisible && zoom >= NEIGHBORHOOD_MARKERS_MIN_ZOOM && !showNeighborhoodMarkers;
        // Legacy badges are a separate always-loaded layer sourced from each
        // neighborhood's static forSaleCount (see setNeighborhoodBadgesVisible's
        // comment) - unrelated to setListings()/activeStatuses by design, for
        // Ask mode's "fixed result set" case. But that also meant they ignored
        // the CNTL drawer's "For Sale" toggle entirely: turning For Sale off
        // only clears `activeStatuses`, which this check never looked at, so a
        // stale count bubble kept showing at zoom 12-13.5. Only show them when
        // For Sale is actually on.
        const showBadges = !this.state.badgesForceHidden && this.state.activeStatuses.has('Active') && zoom >= 12 && !showNeighborhoodMarkers;
        // Both booleans only change at a handful of zoom thresholds (12, 13.5/
        // 14) - skip both marker loops on every other zoom_changed tick, same
        // "did anything actually change" idea as updateListingLayer's signature
        // check.
        if (this.lastVisibilityState?.showLabels === showLabels && this.lastVisibilityState?.showBadges === showBadges) {
            return;
        }
        this.lastVisibilityState = { showLabels, showBadges };
        for (const label of this.state.labels.values()) {
            label.setVisible(showLabels);
        }
        for (const badge of this.state.badges.values()) {
            badge.setVisible(showBadges);
        }
    }
    clearAll() {
        this.clearNeighborhoods();
        this.clearListings();
    }
    fitBounds(bounds) {
        if (this.state.map) {
            this.state.map.fitBounds(bounds);
        }
    }
    panTo(position) {
        if (this.state.map) {
            this.state.map.panTo(position);
        }
    }
    setZoom(zoom) {
        if (this.state.map) {
            this.state.map.setZoom(zoom);
        }
    }
    /**
     * Show/update/hide the "My Location" blue-dot marker. Pass null to remove
     * it (e.g. when the user toggles location off). `heading` is GPS-derived
     * (position.coords.heading) - only populated while physically moving, so
     * the direction cone is hidden whenever it's null/undefined, matching
     * Google Maps' own behavior rather than showing a stale or fabricated
     * direction. `accuracy` (meters) draws the translucent radius circle
     * Google's product also shows; omit to skip it.
     *
     * Created lazily on first call and reused on subsequent calls (position
     * updated in place, heading rotation updated via CSS transform) rather
     * than recreated every time, since watchPosition can fire frequently.
     */
    async setUserLocationMarker(data) {
        if (!this.state.map)
            return;
        if (!data) {
            if (this.userLocationMarker)
                this.userLocationMarker.map = null;
            this.userLocationAccuracyCircle?.setMap(null);
            this.userLocationMarker = null;
            this.userLocationAccuracyCircle = null;
            this.userLocationHeadingEl = null;
            return;
        }
        const position = { lat: data.lat, lng: data.lng };
        if (!this.userLocationMarker) {
            const { AdvancedMarkerElement } = await this.getMarkerLibrary();
            // 40x40 container, dot dead-center. The heading cone lives inside a
            // full-size rotator div whose default (0deg) transform-origin is its
            // own center - which is the same point the dot sits on - so rotating
            // the rotator swings the cone around the dot's exact center with no
            // manual transform-origin offset math.
            const content = document.createElement('div');
            content.style.cssText = `
        position: relative;
        width: 40px;
        height: 40px;
        pointer-events: none;
      `;
            const rotator = document.createElement('div');
            rotator.style.cssText = `
        position: absolute;
        inset: 0;
        display: none;
        transform: rotate(0deg);
      `;
            const cone = document.createElement('div');
            cone.style.cssText = `
        position: absolute;
        top: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: 10px solid transparent;
        border-right: 10px solid transparent;
        border-bottom: 20px solid rgba(66, 133, 244, 0.35);
      `;
            rotator.appendChild(cone);
            content.appendChild(rotator);
            const dot = document.createElement('div');
            dot.style.cssText = `
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background: #4285F4;
        border: 2px solid #fff;
        box-shadow: 0 1px 4px rgba(0,0,0,0.4);
      `;
            content.appendChild(dot);
            this.userLocationHeadingEl = rotator;
            this.userLocationMarker = new AdvancedMarkerElement({
                position,
                map: this.state.map,
                content,
                zIndex: 1000, // above everything else - this is the user's own position
            });
            this.userLocationAccuracyCircle = new google.maps.Circle({
                map: this.state.map,
                center: position,
                radius: data.accuracy ?? 0,
                fillColor: '#4285F4',
                fillOpacity: 0.12,
                strokeColor: '#4285F4',
                strokeOpacity: 0.25,
                strokeWeight: 1,
                clickable: false,
            });
        }
        else {
            this.userLocationMarker.position = position;
            this.userLocationAccuracyCircle?.setCenter(position);
        }
        if (data.accuracy != null) {
            this.userLocationAccuracyCircle?.setRadius(data.accuracy);
        }
        if (this.userLocationHeadingEl) {
            if (data.heading != null && !Number.isNaN(data.heading)) {
                this.userLocationHeadingEl.style.display = 'block';
                this.userLocationHeadingEl.style.transform = `rotate(${data.heading}deg)`;
            }
            else {
                this.userLocationHeadingEl.style.display = 'none';
            }
        }
    }
    setMapType(type) {
        if (this.state.map) {
            this.state.map.setMapTypeId(type);
            // Building fill opacity depends on map type (roadmap vs satellite/hybrid) - redraw immediately
            this.redrawBuildingOverlays();
        }
    }
    getMap() {
        return this.state.map;
    }
    getZoom() {
        return this.state.map?.getZoom() ?? DEFAULT_ZOOM;
    }
    destroy() {
        this.clearAll();
        this.state.map = null;
    }
}
//# sourceMappingURL=Map.js.map